import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectGroup, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Users, Search, CheckCircle, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';


import { API, errorText, newIdempotencyKey, blobErrorText } from '@/utils/api';

// Entry-type badges for outstanding-ledger rows. Types are auto-classified at
// import from the reference text (backend classify_outstanding_entry) — these
// make a CD-short or a GR credit note recognisable at a glance instead of
// rendering like a plain invoice/credit.
const ENTRY_TYPE_BADGES = {
  gr_credit_note: { label: 'GR Credit Note', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  payment_received: { label: 'Payment', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  credit_note: { label: 'Credit Note', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  credit_balance: { label: 'Credit Bal', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  short_payment: { label: 'Short', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  cd_short_payment: { label: 'CD Short', cls: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300' },
  cd_disallowed: { label: 'CD Disallowed', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  rate_difference: { label: 'Rate Diff', cls: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  debit_note: { label: 'Debit Note', cls: 'bg-red-100 text-red-800 border-red-300' },
  interest: { label: 'Interest', cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  clearing_debit: { label: 'Clearing', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  tds_certificate: { label: 'TDS Cert', cls: 'bg-teal-100 text-teal-800 border-teal-300' },
};

const EntryTypeBadge = ({ type, grLinked }) => {
  const b = type ? ENTRY_TYPE_BADGES[type] : null;
  if (!b) return null;
  return (
    <span className={`inline-block ml-1.5 px-1.5 py-0.5 rounded border text-[9px] font-semibold leading-none whitespace-nowrap align-middle ${b.cls}`}>
      {b.label}{grLinked ? ' · GR' : ''}
    </span>
  );
};

export default function Outstanding() {
  const { user, selectedDivision } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [paidInvoices, setPaidInvoices] = useState([]); // New: paid invoices
  const [credits, setCredits] = useState([]);
  const [debits, setDebits] = useState([]); // Debit notes (additional amounts owed by customer)
  const [customers120Plus, setCustomers120Plus] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');

  // Statement of Account (open items) dialog — customer-facing artefact,
  // generated by admin/accounts only (backend enforces the same).
  const canGenerateStatement = user?.role === 'admin' || user?.role === 'accounts';
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtCustomerId, setStmtCustomerId] = useState('');
  const [stmtDownloading, setStmtDownloading] = useState(false);
  const [regionFilter, setRegionFilter] = useState('all');
  const [customerCodeFilter, setCustomerCodeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('outstanding'); // New: 'outstanding', 'paid', 'all'
  const [sortBy, setSortBy] = useState('date_asc'); // date_asc, date_desc, amount_high, amount_low
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [customerMap, setCustomerMap] = useState({});
  const [selectedCredits, setSelectedCredits] = useState([]); // [{transaction_id, label, amount}]
  const [selectedDebits, setSelectedDebits] = useState([]); // Debit notes to settle with payment
  const [availableCredits, setAvailableCredits] = useState([]);
  const [availableDebits, setAvailableDebits] = useState([]);
  const [regions, setRegions] = useState([]);
  const [customerCodes, setCustomerCodes] = useState([]);
  
  // Edit/Delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  
  // Multi-invoice payment mode (within Record Payment dialog)
  const [multiInvoiceMode, setMultiInvoiceMode] = useState(false);
  const [customerInvoicesForPayment, setCustomerInvoicesForPayment] = useState([]);
  
  // Square Off / Reconcile dialog state
  const [squareOffDialogOpen, setSquareOffDialogOpen] = useState(false);
  const [squareOffInvoice, setSquareOffInvoice] = useState(null);
  const [squareOffCredit, setSquareOffCredit] = useState(null);
  const [squareOffLoading, setSquareOffLoading] = useState(false);
  const [squareOffSearch, setSquareOffSearch] = useState('');
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  // Server-side duplicate guard for the payment being entered (audit F-11).
  const paymentKeyRef = useRef(newIdempotencyKey());
  
  // Credit adjustment in payment dialog
  const [selectedCreditForPayment, setSelectedCreditForPayment] = useState(null);
  
  // Division CD Rates (fetched from database)
  const [divisionCdRates, setDivisionCdRates] = useState({});
  
  const [paymentForm, setPaymentForm] = useState({
    payments: [
      {
        payment_amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'neft',
        payment_reference: '',
        remarks: ''
      }
    ],
    cd_policy: 'no_cd',
    close_cd_after_management_approval: false,
    allow_cd_with_short_payment: false
  });
  
  // CD Policies for the selected invoice
  const [applicableCdPolicies, setApplicableCdPolicies] = useState([]);
  const [loadingCdPolicies, setLoadingCdPolicies] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmDescription, setConfirmDescription] = useState('');
  const confirmActionRef = useRef(null);

  const requestConfirm = ({ title, description, onConfirm }) => {
    setConfirmTitle(title);
    setConfirmDescription(description || 'Please confirm');
    confirmActionRef.current = onConfirm;
    setConfirmOpen(true);
  };
  
  // Fetch applicable CD policies for an invoice based on division + customer region
  const fetchApplicableCdPolicies = useCallback(async (invoice) => {
    if (!invoice) return;
    setLoadingCdPolicies(true);
    try {
      // Get the customer's region
      const customer = customers.find(c => c.customer_id === invoice.customer_id);
      const region = customer?.region || invoice.region;
      const division = invoice.division || selectedDivision;
      
      // Fetch CD policy for this division + region
      const response = await axios.get(
        `${API}/config/cd-policy-for-invoice?division=${encodeURIComponent(division || '')}&region=${encodeURIComponent(region || '')}&customer_id=${encodeURIComponent(invoice.customer_id || '')}`,
        { withCredentials: true }
      );
      
      if (response.data.policies && response.data.policies.length > 0) {
        setApplicableCdPolicies(response.data.policies);
        // Set the first policy as default if available
        setPaymentForm(prev => ({
          ...prev,
          cd_policy: response.data.policies[0].policy_code || 'no_cd'
        }));
      } else {
        setApplicableCdPolicies([]);
        setPaymentForm(prev => ({ ...prev, cd_policy: 'no_cd' }));
      }
    } catch (error) {
      console.error('Failed to fetch CD policies:', error);
      setApplicableCdPolicies([]);
    } finally {
      setLoadingCdPolicies(false);
    }
  }, [customers, selectedDivision]);
  
  const fetchCustomers120Plus = useCallback(async () => {
    try {
      const divParam = selectedDivision && selectedDivision !== 'all' ? `?division=${encodeURIComponent(selectedDivision)}` : '';
      const response = await axios.get(`${API}/analytics/customers-120-plus${divParam}`, {
        withCredentials: true
      });
      setCustomers120Plus(response.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch 120+ customers:', error);
    }
  }, [selectedDivision]);
  
  // Fetch division CD rates and customer-specific rates from database
  const fetchDivisionCdRates = useCallback(async () => {
    try {
      const [divisionResponse, customerResponse] = await Promise.all([
        axios.get(`${API}/config/division-cd-rates`, { withCredentials: true }),
        axios.get(`${API}/config/customer-cd-rates`, { withCredentials: true }).catch(() => ({ data: { customer_cd_rates: [] } }))
      ]);
      
      // Create maps for division and division+region rates
      const divisionRatesMap = {};
      const divisionRegionRatesMap = {};
      const customerRatesMap = {};
      
      (divisionResponse.data.division_cd_rates || []).forEach(rate => {
        divisionRatesMap[rate.division] = rate;
      });
      
      (divisionResponse.data.division_region_cd_rates || []).forEach(rate => {
        const key = `${rate.division}|${rate.region}`;
        divisionRegionRatesMap[key] = rate;
      });
      
      // Customer-specific rates (highest priority)
      (customerResponse.data.customer_cd_rates || []).forEach(rate => {
        customerRatesMap[rate.customer_id] = rate;
      });
      
      setDivisionCdRates({ 
        divisions: divisionRatesMap, 
        regions: divisionRegionRatesMap,
        customers: customerRatesMap
      });
    } catch (error) {
      console.error('Failed to fetch CD rates:', error);
    }
  }, []);
  
  const fetchOutstandingData = useCallback(async () => {
    try {
      // Fetch customers first
      const custDivParam = selectedDivision && selectedDivision !== 'all' ? `?division=${encodeURIComponent(selectedDivision)}` : '';
      const customersResponse = await axios.get(`${API}/customers${custDivParam}`, {
        withCredentials: true
      });

      setCustomers(customersResponse.data);
      
      // Create customer map including region
      const custMap = {};
      customersResponse.data.forEach(customer => {
        custMap[customer.customer_id] = {
          name: customer.name,
          customer_code: customer.customer_code || '',
          region: customer.region || ''
        };
      });
      setCustomerMap(custMap);

      // Extract unique regions and customer codes from customers
      const uniqueRegions = [...new Set(customersResponse.data.map(c => c.region).filter(Boolean))].sort();
      const uniqueCodes = [...new Set(customersResponse.data.map(c => c.customer_code).filter(Boolean))].sort();
      setRegions(uniqueRegions);
      setCustomerCodes(uniqueCodes);

      // Reset filter if the selected customer no longer exists
      if (customerFilter !== 'all' && !customersResponse.data.some(c => c.customer_id === customerFilter)) {
        setCustomerFilter('all');
      }
      
      // Fetch sales with division filter
      const divisionParam = selectedDivision && selectedDivision !== 'all' ? `?division=${encodeURIComponent(selectedDivision)}` : '';
      const response = await axios.get(`${API}/sales${divisionParam}`, {
        withCredentials: true
      });
      
      // Filter only outstanding invoices (pending + overdue + paid_with_short + any with short balance) and exclude credits
      // Also include any invoice with remaining short_payment_balance even if status is "paid"
      const outstanding = response.data.filter(
        sale => {
          // Exclude credit notes and entries without invoice numbers
          if (sale.is_credit_note || !sale.invoice_number) return false;
          
          // Include if status is pending, overdue, or paid_with_short
          if (sale.payment_status === 'pending' || sale.payment_status === 'overdue' || sale.payment_status === 'paid_with_short') {
            return true;
          }
          
          // Also include if there's a short payment balance > 0 (regardless of status)
          if (sale.short_payment_balance > 0 || sale.is_short_payment) {
            return true;
          }
          
          return false;
        }
      );

      // Filter paid and partially paid invoices (any invoice with payments)
      const paidAndPartial = response.data.filter(
        sale => !sale.is_credit_note
          && sale.invoice_number
          && (sale.payment_status === 'paid' || 
              (sale.payments && sale.payments.length > 0) ||
              (sale.paid_amount && sale.paid_amount > 0))
      );

      // Capture credits separately (credit notes - we owe customer)
      // Credit = is_credit_note OR negative amount non-invoice entries
      const creditsData = response.data.filter(sale => {
        if (sale.is_credit_note) return true;
        // Non-invoice entries with negative amounts are also credits
        if (!sale.invoice_number && (sale.amount || 0) < 0) return true;
        return false;
      });

      // Capture debit balances (non-invoice positive amounts customer owes)
      // Debit = NOT an invoice, NOT a credit, positive amount
      // Debit-side adjustments (entries that are receivable but aren't sale
      // invoices). Driven by the auto-classified transaction_type — the old
      // heuristic only caught refs containing "dn", so clearing debits, rate
      // differences and interest rows hid among invoices.
      const DEBIT_ADJUSTMENT_TYPES = ['debit_note', 'interest', 'cd_disallowed', 'rate_difference', 'clearing_debit'];
      const debitsData = response.data.filter(sale => {
        if (sale.is_credit_note) return false;
        if (sale.transaction_type === 'debit_note' || sale.is_debit_note) return true;
        return DEBIT_ADJUSTMENT_TYPES.includes(sale.transaction_type) && (sale.amount || 0) > 0;
      });

      setCredits(creditsData);
      setDebits(debitsData);
      setPaidInvoices(paidAndPartial);
      
      // Sort by invoice date (oldest first) - default sort
      outstanding.sort((a, b) => {
        const dateA = new Date(a.invoice_date);
        const dateB = new Date(b.invoice_date);
        return dateA - dateB;
      });
      
      setInvoices(outstanding);
      
      // Get customers with 120+ day invoices
      await fetchCustomers120Plus();
    } catch (error) {
      console.error('Failed to fetch outstanding data:', error);
      toast.error('Failed to load outstanding data');
    } finally {
      setLoading(false);
    }
  }, [selectedDivision, customerFilter, fetchCustomers120Plus]);
  
  useEffect(() => {
    fetchOutstandingData();
    fetchDivisionCdRates();
  }, [fetchOutstandingData, fetchDivisionCdRates]);
  
  const handleRecordPayment = async () => {
    if (!selectedInvoice || isSubmittingPayment) return;
    setIsSubmittingPayment(true);

    try {
      // Collect credit IDs from both selectedCredits AND payment entries with credit_adjustment mode
      const creditIdsFromPayments = paymentForm.payments
        .filter(p => p.payment_mode === 'credit_adjustment' && p.credit_id)
        .map(p => p.credit_id);
      
      const allCreditIds = [
        ...selectedCredits.map(c => c.transaction_id),
        ...creditIdsFromPayments
      ];
      
      // Remove duplicates
      const uniqueCreditIds = [...new Set(allCreditIds)];
      
      // Collect debit IDs to settle with this payment
      const debitIds = selectedDebits.map(d => d.transaction_id);
      
      const response = await axios.post(
        `${API}/sales/record-payment`,
        null,
        {
          params: {
            transaction_id: selectedInvoice.transaction_id,
            payments: JSON.stringify(paymentForm.payments),
            credit_ids: JSON.stringify(uniqueCreditIds),
            debit_ids: JSON.stringify(debitIds),
            cd_policy: paymentForm.cd_policy,
            close_cd_after_management_approval: paymentForm.close_cd_after_management_approval,
            allow_cd_with_short_payment: paymentForm.allow_cd_with_short_payment,
            idempotency_key: paymentKeyRef.current
          },
          withCredentials: true
        }
      );

      const result = response.data;
      const paidAmount = result.paid_amount || 0;

      let message = 'Payment recorded\n';
      message += `Invoice Total: ₹${result.invoice_amount.toLocaleString()}\n`;
      message += `Paid so far: ₹${paidAmount.toLocaleString()}\n`;
      if (result.cash_discount_amount > 0) {
        message += `CD Amount: ₹${result.cash_discount_amount.toLocaleString()}\n`;
      }
      if (result.short_payment_balance > 0) {
        message += `Short Payment Balance: ₹${result.short_payment_balance.toLocaleString()}\n`;
      }
      if (result.debits_settled > 0) {
        message += `Debits Settled: ₹${result.debits_settled.toLocaleString()}\n`;
      }
      message += `Status: ${result.payment_status}`;

      toast.success(message);
      paymentKeyRef.current = newIdempotencyKey();

      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
      setSelectedDebits([]); // Reset selected debits
      setPaymentForm({
        payments: [
          {
            payment_amount: '',
            payment_date: new Date().toISOString().split('T')[0],
            payment_mode: 'neft',
            payment_reference: '',
            remarks: ''
          }
        ],
        cd_policy: 'no_cd',
        close_cd_after_management_approval: false,
        allow_cd_with_short_payment: false
      });

      // Refresh data
      fetchOutstandingData();
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error(errorText(error) || 'Failed to record payment');
    } finally {
      setIsSubmittingPayment(false);
    }
  };
  
  const fetchAvailableCredits = async (customerId) => {
    try {
      const resp = await axios.get(`${API}/customers/${customerId}/available-credits`, {
        withCredentials: true
      });
      return resp.data || [];
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      toast.error('Could not load available credits — do not settle this invoice until they load, or an existing credit may be missed.');
      return [];
    }
  };

  const fetchAvailableDebits = async (customerId) => {
    try {
      const resp = await axios.get(`${API}/customers/${customerId}/available-debits`, {
        withCredentials: true
      });
      return resp.data || [];
    } catch (error) {
      console.error('Failed to fetch debits:', error);
      toast.error('Could not load available debits — they may be missing from this settlement.');
      return [];
    }
  };

  const handleDownloadStatement = async (fmt = 'pdf') => {
    if (!stmtCustomerId) return;
    setStmtDownloading(true);
    try {
      const params = { format: fmt };
      if (selectedDivision && selectedDivision !== 'all') params.division = selectedDivision;
      const res = await axios.get(`${API}/customers/${stmtCustomerId}/account-statement`, {
        params, withCredentials: true, responseType: 'blob', timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cust = [...invoices, ...credits, ...paidInvoices, ...debits]
        .find(r => r.customer_id === stmtCustomerId);
      const safeName = (cust?.customer_name || stmtCustomerId).replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 30);
      link.setAttribute('download', `Statement_${safeName}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
      setStmtOpen(false);
    } catch (err) {
      toast.error(`Statement failed: ${(await blobErrorText(err)) || err.message || 'Unknown error'}`);
    } finally {
      setStmtDownloading(false);
    }
  };

  const canRecordPayment = user?.role === 'admin' || user?.role === 'accounts';
  const canEdit = user?.role === 'admin' || user?.role === 'accounts';
  const canDelete = user?.role === 'admin';

  // Edit transaction handlers
  const openEditDialog = (transaction) => {
    setEditingTransaction(transaction);
    setEditForm({
      amount: transaction.amount,
      invoice_number: transaction.invoice_number,
      invoice_date: transaction.invoice_date?.split('T')[0] || '',
      due_date: transaction.due_date?.split('T')[0] || '',
      payment_status: transaction.payment_status || 'pending',
      remarks: transaction.remarks || '',
      credit_note_reason: transaction.credit_note_reason || '',
      credit_note_type: transaction.credit_note_type || 'credit',
      clearPayments: false
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTransaction = async () => {
    if (!editingTransaction) return;
    
    try {
      const updates = {};
      
      // Only include amount if it's a valid number
      if (editForm.amount !== '' && editForm.amount !== null && editForm.amount !== undefined) {
        updates.amount = parseFloat(editForm.amount);
      }
      
      // Include other fields
      if (editForm.invoice_number) updates.invoice_number = editForm.invoice_number;
      if (editForm.invoice_date) updates.invoice_date = editForm.invoice_date;
      if (editForm.due_date) updates.due_date = editForm.due_date;
      if (editForm.payment_status) updates.payment_status = editForm.payment_status;
      if (editForm.remarks !== undefined) updates.remarks = editForm.remarks;
      
      // If clearing payments, send empty array and set status to pending
      if (editForm.clearPayments) {
        updates.payments = [];
        updates.payment_status = 'pending';
        updates.paid_amount = 0;
      }
      
      if (editingTransaction.is_credit_note) {
        updates.credit_note_reason = editForm.credit_note_reason;
        updates.credit_note_type = editForm.credit_note_type;
      }
      
      // Make sure we have something to update
      if (Object.keys(updates).length === 0) {
        toast.error('No changes to save');
        return;
      }
      
      await axios.put(`${API}/sales/${editingTransaction.transaction_id}`, updates, {
        withCredentials: true
      });
      
      toast.success('Transaction updated successfully');
      setEditDialogOpen(false);
      setEditingTransaction(null);
      fetchOutstandingData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(errorText(error) || 'Failed to update transaction');
    }
  };

  // Delete transaction handlers
  const openDeleteDialog = (transaction) => {
    setDeletingTransaction(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTransaction = async () => {
    if (!deletingTransaction) return;
    
    try {
      await axios.delete(`${API}/sales/${deletingTransaction.transaction_id}`, {
        withCredentials: true
      });
      
      toast.success('Transaction deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingTransaction(null);
      fetchOutstandingData();
    } catch (error) {
      toast.error(errorText(error) || 'Failed to delete transaction');
    }
  };

  // Multi-invoice payment helpers
  const loadCustomerInvoicesForPayment = (customerId) => {
    // Get all outstanding invoices for this customer
    const custInvoices = invoices.filter(inv => 
      inv.customer_id === customerId && 
      (inv.payment_status === 'pending' || inv.payment_status === 'overdue')
    ).map(inv => ({
      transaction_id: inv.transaction_id,
      invoice_number: inv.invoice_number,
      invoice_date: inv.invoice_date,
      amount: inv.amount,
      paid_amount: inv.paid_amount || 0,
      cash_discount_amount: inv.cash_discount_amount || 0,
      remaining: inv.amount - (inv.paid_amount || 0) - (inv.cash_discount_amount || 0),
      division: inv.division,  // Required for CD rate lookup
      region: inv.region,      // Helpful for CD rate calculation
      customer_id: inv.customer_id,
      selected: false,
      allocated: 0,
      cd_policy: 'no_cd',      // Initialize CD policy for dropdown
      cd_approval: false,
      allow_short: false,
      cd_amount: 0             // CD amount when policy is selected
    }));
    
    setCustomerInvoicesForPayment(custInvoices);
  };

  const toggleInvoiceForPayment = (transactionId) => {
    const totalPayment = paymentForm.payments.reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0);
    
    setCustomerInvoicesForPayment(prev => {
      // First, toggle the selection
      const updated = prev.map(inv => 
        inv.transaction_id === transactionId 
          ? { ...inv, selected: !inv.selected }
          : inv
      );
      
      // Calculate already allocated amount (excluding the toggled invoice if it was just selected)
      const toggledInv = updated.find(inv => inv.transaction_id === transactionId);
      const otherAllocated = updated
        .filter(inv => inv.selected && inv.transaction_id !== transactionId)
        .reduce((sum, inv) => sum + (inv.allocated || 0), 0);
      
      // If selecting (not deselecting), set allocation to 0 - user will enter manually
      if (toggledInv?.selected) {
        return updated.map(inv => 
          inv.transaction_id === transactionId 
            ? { ...inv, allocated: 0 }
            : inv
        );
      } else {
        // Deselecting - set allocation to 0
        return updated.map(inv => 
          inv.transaction_id === transactionId 
            ? { ...inv, allocated: 0 }
            : inv
        );
      }
    });
  };

  const selectAllInvoicesForPayment = () => {
    // Select all invoices but set allocation to 0 - user will enter manually
    setCustomerInvoicesForPayment(prev => prev.map(inv => ({ 
      ...inv, 
      selected: true, 
      allocated: 0 
    })));
  };

  const clearAllInvoicesForPayment = () => {
    setCustomerInvoicesForPayment(prev => prev.map(inv => ({ ...inv, selected: false, allocated: 0 })));
  };

  const autoAllocatePaymentFIFO = () => {
    const totalPayment = paymentForm.payments.reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0);
    let remaining = totalPayment;
    
    // Sort by date (oldest first) for FIFO
    const sorted = [...customerInvoicesForPayment].sort((a, b) => 
      new Date(a.invoice_date) - new Date(b.invoice_date)
    );
    
    const updated = sorted.map(inv => {
      if (remaining <= 0) return { ...inv, selected: false, allocated: 0 };
      
      const toAllocate = Math.min(remaining, inv.remaining);
      remaining -= toAllocate;
      
      return {
        ...inv,
        selected: toAllocate > 0,
        allocated: toAllocate
      };
    });
    
    // Restore original order
    setCustomerInvoicesForPayment(customerInvoicesForPayment.map(inv => 
      updated.find(u => u.transaction_id === inv.transaction_id) || inv
    ));
  };

  const handleMultiInvoicePayment = async () => {
    // Prevent double submission
    if (isSubmittingPayment) {
      return;
    }
    
    let selectedInvs = customerInvoicesForPayment.filter(inv => inv.selected);
    
    if (selectedInvs.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }
    
    const totalPayment = paymentForm.payments.reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0);
    
    if (totalPayment <= 0) {
      toast.error('Please enter a payment amount');
      return;
    }
    
    // Calculate totals for validation
    let totalAllocated = selectedInvs.reduce((sum, inv) => sum + (inv.allocated || 0), 0);
    const totalCdAmount = selectedInvs.reduce((sum, inv) => {
      if (inv.cd_policy && inv.cd_policy !== 'no_cd') {
        return sum + (inv.cd_amount || 0);
      }
      return sum;
    }, 0);
    
    // Allocations + selected debits must account for the payment: the server
    // pays the debits from whatever is left unallocated.
    const debitTotal = selectedDebits.reduce((sum, d) => sum + (d.amount || 0), 0);
    if (Math.abs(totalAllocated + debitTotal - totalPayment) > 10) {
      toast.error(`Total allocated (₹${totalAllocated.toLocaleString()})${debitTotal ? ` + debits (₹${debitTotal.toLocaleString()})` : ''} must equal payment amount (₹${totalPayment.toLocaleString()}). Please adjust allocations.`);
      return;
    }
    
    // Set submitting state to prevent double clicks
    setIsSubmittingPayment(true);
    
    try {
      // Include ALL selected invoices - even those with 0 allocation (for CD application)
      // CD applies to all selected invoices regardless of payment amount
      const invoiceAllocations = selectedInvs.map(inv => ({
        transaction_id: inv.transaction_id,
        allocated: inv.allocated || 0,
        cd_policy: inv.cd_policy || 'no_cd',
        cd_approval: inv.cd_approval || false,
        allow_short: inv.allow_short || false,
        division: inv.division
      }));
      
      // Collect selected credit and debit IDs
      const creditIds = selectedCredits.map(c => c.transaction_id);
      const debitIds = selectedDebits.map(d => d.transaction_id);

      const response = await axios.post(
        `${API}/sales/bulk-payment`,
        null,
        {
          params: {
            customer_id: selectedInvoice.customer_id,
            total_amount: totalPayment,
            payment_date: paymentForm.payments[0].payment_date,
            payment_mode: paymentForm.payments[0].payment_mode,
            payment_reference: paymentForm.payments[0].payment_reference || '',
            remarks: paymentForm.payments[0].remarks || 'Multi-invoice bulk payment',
            invoice_allocations: JSON.stringify(invoiceAllocations),
            credit_ids: JSON.stringify(creditIds),
            debit_ids: JSON.stringify(debitIds),
            idempotency_key: paymentKeyRef.current
          },
          withCredentials: true
        }
      );
      
      const result = response.data;
      const successCount = result.invoice_results.filter(r => r.status !== 'error').length;
      const failCount = result.invoice_results.filter(r => r.status === 'error').length;
      
      // Build success message
      let message = `Bulk payment recorded: ₹${totalPayment.toLocaleString()} (ID: ${result.bulk_payment_id})`;
      if (result.total_cd_amount > 0) {
        message += ` | CD: ₹${result.total_cd_amount.toLocaleString()}`;
      }
      if (result.total_credits_applied > 0) {
        message += ` | Credits Applied: ₹${result.total_credits_applied.toLocaleString()}`;
      }
      if (result.total_debits_settled > 0) {
        message += ` | Debits Settled: ₹${result.total_debits_settled.toLocaleString()}`;
      }
      if (result.credit_created) {
        message += ` | Excess Credit: ₹${result.credit_created.amount.toLocaleString()}`;
      }
      
      toast.success(message);
      paymentKeyRef.current = newIdempotencyKey();
      
      if (failCount > 0) {
        toast.warning(`${failCount} invoice(s) failed to process`);
      }
      
      // Reset form and refresh
      setPaymentDialogOpen(false);
      setMultiInvoiceMode(false);
      setCustomerInvoicesForPayment([]);
      setSelectedCredits([]);
      setSelectedDebits([]);
      setPaymentForm({
        payments: [
          {
            payment_amount: '',
            payment_date: new Date().toISOString().split('T')[0],
            payment_mode: 'neft',
            payment_reference: '',
            remarks: ''
          }
        ],
        cd_policy: 'no_cd',
        close_cd_after_management_approval: false,
        allow_cd_with_short_payment: false
      });
      fetchOutstandingData();
      
    } catch (error) {
      console.error('Bulk payment failed:', error);
      toast.error(errorText(error) || 'Failed to record bulk payment');
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const openPaymentDialog = async (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentDialogOpen(true);
    setSelectedCredits([]);
    setSelectedDebits([]);
    setMultiInvoiceMode(false);
    setIsSubmittingPayment(false); // Reset submitting state when dialog opens
    paymentKeyRef.current = newIdempotencyKey();
    
    // Reset payment form
    setPaymentForm({
      payments: [
        {
          payment_amount: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_mode: 'neft',
          payment_reference: '',
          remarks: ''
        }
      ],
      cd_policy: 'no_cd',
      close_cd_after_management_approval: false
    });
    
    // Fetch applicable CD policies for this invoice
    fetchApplicableCdPolicies(invoice);

    // Prefetch credits and debits for this customer
    if (invoice?.customer_id && canRecordPayment) {
      const [creditsList, debitsList] = await Promise.all([
        fetchAvailableCredits(invoice.customer_id),
        fetchAvailableDebits(invoice.customer_id)
      ]);
      setAvailableCredits(creditsList);
      setAvailableDebits(debitsList);
      // Also load all customer invoices for multi-invoice mode
      loadCustomerInvoicesForPayment(invoice.customer_id);
    }
  };
  
  // Filter invoices by search, customer, region, and customer code
  const filteredInvoices = invoices.filter(invoice => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (invoice.invoice_number || '').toLowerCase().includes(searchLower) ||
      (invoice.customer_name || '').toLowerCase().includes(searchLower) ||
      (invoice.customer_id || '').toLowerCase().includes(searchLower) ||
      (invoice.customer_code || '').toLowerCase().includes(searchLower)
    );

    const matchesCustomer = customerFilter === 'all' || invoice.customer_id === customerFilter;
    const matchesRegion = regionFilter === 'all' || invoice.region === regionFilter;
    const matchesCode = customerCodeFilter === 'all' || invoice.customer_code === customerCodeFilter;
    
    return matchesSearch && matchesCustomer && matchesRegion && matchesCode;
  });

  // Filter paid invoices with same logic
  const filteredPaidInvoices = paidInvoices.filter(invoice => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (invoice.invoice_number || '').toLowerCase().includes(searchLower) ||
      (invoice.customer_name || '').toLowerCase().includes(searchLower) ||
      (invoice.customer_id || '').toLowerCase().includes(searchLower) ||
      (invoice.customer_code || '').toLowerCase().includes(searchLower)
    );

    const matchesCustomer = customerFilter === 'all' || invoice.customer_id === customerFilter;
    const matchesRegion = regionFilter === 'all' || invoice.region === regionFilter;
    const matchesCode = customerCodeFilter === 'all' || invoice.customer_code === customerCodeFilter;
    
    return matchesSearch && matchesCustomer && matchesRegion && matchesCode;
  });

  // Sort the filtered invoices based on sortBy
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    switch (sortBy) {
      case 'amount_high':
        return b.amount - a.amount;
      case 'amount_low':
        return a.amount - b.amount;
      case 'date_desc':
        return new Date(b.invoice_date) - new Date(a.invoice_date);
      case 'date_asc':
      default:
        return new Date(a.invoice_date) - new Date(b.invoice_date);
    }
  });

  // Sort paid invoices (newest first by default)
  const sortedPaidInvoices = [...filteredPaidInvoices].sort((a, b) => {
    switch (sortBy) {
      case 'amount_high':
        return b.amount - a.amount;
      case 'amount_low':
        return a.amount - b.amount;
      case 'date_asc':
        return new Date(a.invoice_date) - new Date(b.invoice_date);
      case 'date_desc':
      default:
        return new Date(b.invoice_date) - new Date(a.invoice_date);
    }
  });

  const creditReasonOptions = [
    'Goods return',
    'Defective',
    'Shortage',
    'Incentive',
    'Scheme',
    'Discount'
  ];

  const filteredCredits = credits.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (item.invoice_number || '').toLowerCase().includes(searchLower) ||
      (item.customer_name || '').toLowerCase().includes(searchLower) ||
      (item.customer_id || '').toLowerCase().includes(searchLower)
    );

    const matchesCustomer = customerFilter === 'all' || item.customer_id === customerFilter;
    return matchesSearch && matchesCustomer;
  });
  
  const getDaysFromInvoice = (invoiceDate) => {
    const now = new Date();
    const date = new Date(invoiceDate);
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };
  
  const getStatusColor = (days) => {
    if (days <= 45) return 'bg-blue-100 text-blue-800';
    if (days <= 90) return 'bg-yellow-100 text-yellow-800';
    if (days <= 120) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const clearCreditReason = async (transactionId) => {
    try {
      await axios.put(
        `${API}/sales/${transactionId}/credit-reason`,
        null,
        {
          params: { credit_note_reason: '' },  // '' clears; axios drops null params -> 422 (audit F-28)
          withCredentials: true
        }
      );

      setCredits((prev) => prev.map((c) => (c.transaction_id === transactionId ? { ...c, credit_note_reason: null } : c)));
      toast.success('Credit reason cleared');
    } catch (error) {
      console.error('Failed to clear credit reason:', error);
      toast.error(errorText(error) || 'Failed to clear reason');
    }
  };
  
  const canManageCredits = user?.role === 'admin' || user?.role === 'accounts';

  const updateCreditReason = async (transactionId, reason) => {
    try {
      await axios.put(
        `${API}/sales/${transactionId}/credit-reason`,
        null,
        {
          params: { credit_note_reason: reason },
          withCredentials: true
        }
      );

      setCredits((prev) => prev.map((c) => (c.transaction_id === transactionId ? { ...c, credit_note_reason: reason } : c)));
      toast.success('Credit reason saved');
    } catch (error) {
      console.error('Failed to update credit reason:', error);
      toast.error(errorText(error) || 'Failed to save credit reason');
    }
  };

  const applyCreditAsPayment = async (creditTxnId) => {
    try {
      await axios.post(
        `${API}/sales/${creditTxnId}/apply-credit-as-payment`,
        null,
        { withCredentials: true }
      );

      toast.success('Credit applied to invoices (FIFO)');
      fetchOutstandingData();
    } catch (error) {
      console.error('Failed to apply credit as payment:', error);
      toast.error(errorText(error) || 'Failed to apply credit');
    }
  };

  const confirmApplyFifo = (creditTxnId) => {
    requestConfirm({
      title: 'Confirm Apply FIFO',
      description: 'Apply this credit balance to outstanding invoices FIFO (oldest first)?',
      onConfirm: () => applyCreditAsPayment(creditTxnId)
    });
  };

  const confirmUndoApplyFifo = (creditTxnId) => {
    requestConfirm({
      title: 'Confirm Undo',
      description: 'Undo FIFO application for this credit balance?',
      onConfirm: () => undoApplyCredit(creditTxnId)
    });
  };

  const undoApplyCredit = async (creditTxnId) => {
    try {
      await axios.post(
        `${API}/sales/${creditTxnId}/undo-apply-credit`,
        null,
        { withCredentials: true }
      );

      toast.success('Credit application undone');
      fetchOutstandingData();
    } catch (error) {
      console.error('Failed to undo credit:', error);
      toast.error(errorText(error) || 'Failed to undo');
    }
  };

  // Square Off / Reconcile functionality
  const openSquareOffDialog = (invoice = null) => {
    setSquareOffInvoice(invoice);
    setSquareOffCredit(null);
    setSquareOffDialogOpen(true);
  };

  const handleSquareOff = async () => {
    if (!squareOffInvoice || !squareOffCredit) {
      toast.error('Please select both an invoice and a credit to square off');
      return;
    }

    setSquareOffLoading(true);
    try {
      const response = await axios.post(
        `${API}/sales/square-off`,
        null,
        {
          params: {
            invoice_transaction_id: squareOffInvoice.transaction_id,
            credit_transaction_id: squareOffCredit.transaction_id
          },
          withCredentials: true
        }
      );

      const result = response.data;
      
      let message = `Square Off Complete!\n`;
      message += `Applied: ₹${result.applied_amount.toLocaleString()}\n`;
      
      if (result.invoice_remaining > 0) {
        message += `Invoice Balance: ₹${result.invoice_remaining.toLocaleString()} (still outstanding)\n`;
      } else {
        message += `Invoice: Fully settled ✓\n`;
      }
      
      if (result.credit_remaining > 0) {
        message += `Credit Balance: ₹${result.credit_remaining.toLocaleString()} (available for other invoices)`;
      } else {
        message += `Credit: Fully utilized ✓`;
      }

      toast.success(message);
      setSquareOffDialogOpen(false);
      setSquareOffInvoice(null);
      setSquareOffCredit(null);
      fetchOutstandingData();
    } catch (error) {
      console.error('Failed to square off:', error);
      toast.error(errorText(error) || 'Failed to square off');
    } finally {
      setSquareOffLoading(false);
    }
  };

  // Get invoices that have balance remaining (including short payments)
  // Apply ₹10 rounding tolerance
  const ROUNDING_TOLERANCE = 10;
  const invoicesWithBalance = [...invoices, ...paidInvoices].filter(inv => {
    const paid = inv.paid_amount || 0;
    const cd = inv.cash_discount_amount || 0;
    const shortBalance = inv.short_payment_balance || 0;
    const balance = inv.amount - paid - cd;
    // Only include if balance exceeds rounding tolerance OR has explicit short payment balance
    return balance > ROUNDING_TOLERANCE || shortBalance > ROUNDING_TOLERANCE;
  });

  // Get credits that have remaining balance
  const creditsWithBalance = credits.filter(c => {
    if (c.credit_applied && c.credit_remaining <= 0) return false;
    const available = c.credit_remaining !== undefined ? c.credit_remaining : Math.abs(c.amount || 0);
    return available > 0.01;
  });

  // Get debits that have remaining balance (not fully settled)
  const debitsWithBalance = debits.filter(d => {
    if (d.debit_settled) return false;
    const remaining = d.debit_remaining !== undefined ? d.debit_remaining : Math.abs(d.amount || 0);
    return remaining > 0.01;
  });

  // Filter credits for the selected invoice's customer
  const creditsForSelectedInvoice = squareOffInvoice 
    ? creditsWithBalance.filter(c => c.customer_id === squareOffInvoice.customer_id)
    : creditsWithBalance;

  // Filter debits for the selected invoice's customer (works for single and multi-invoice mode)
  const selectedCustomerId = selectedInvoice?.customer_id;
  const debitsForSelectedCustomer = selectedCustomerId
    ? debitsWithBalance.filter(d => d.customer_id === selectedCustomerId)
    : debitsWithBalance;
  
  // Filter credits for the selected customer (for payment dialog)
  const creditsForSelectedCustomer = selectedCustomerId
    ? creditsWithBalance.filter(c => c.customer_id === selectedCustomerId)
    : creditsWithBalance;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>

      </div>
    );
  }
  
  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground mb-1">
            Outstanding Invoices
          </h1>
          <p className="text-muted-foreground text-sm">Invoice-wise outstanding with oldest invoices first</p>
        </div>
        <div className="flex items-center gap-2">
          {canGenerateStatement && (
            <Button
              onClick={() => { setStmtCustomerId(''); setStmtOpen(true); }}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              title="Download a customer's Statement of Account (open items) as PDF or Excel"
            >
              <FileText size={16} className="mr-2" />
              Statement
            </Button>
          )}
          {canRecordPayment && (
            <Button
              onClick={() => openSquareOffDialog()}
              variant="outline"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            >
              <CheckCircle size={16} className="mr-2" />
              Square Off
            </Button>
          )}
        </div>
      </div>

      {/* Statement of Account dialog — admin/accounts only */}
      <Dialog open={stmtOpen && canGenerateStatement} onOpenChange={setStmtOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={16} className="text-indigo-600" />
              Statement of Account — Open Items
            </DialogTitle>
            <DialogDescription>
              Chronological Dr/Cr statement with running balance, per the principal's
              latest outstanding report plus agency-recorded receipts. The closing
              balance ties to the customer's net outstanding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Customer</Label>
              <Select value={stmtCustomerId} onValueChange={setStmtCustomerId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Pick a customer" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const seen = new Map();
                    [...invoices, ...credits, ...paidInvoices, ...debits].forEach(r => {
                      if (r.customer_id && !seen.has(r.customer_id)) {
                        seen.set(r.customer_id, {
                          id: r.customer_id,
                          name: r.customer_name || 'Unknown',
                          code: r.customer_code || '',
                        });
                      }
                    });
                    return [...seen.values()]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}{c.code ? ` (${c.code})` : ''}
                        </SelectItem>
                      ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            {selectedDivision && selectedDivision !== 'all' && (
              <p className="text-xs text-muted-foreground">Division: {selectedDivision}</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setStmtOpen(false)} disabled={stmtDownloading}>Cancel</Button>
            <Button
              variant="outline"
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              disabled={!stmtCustomerId || stmtDownloading}
              onClick={() => handleDownloadStatement('excel')}
              title="Editable copy — remove line items before sharing with the customer"
            >
              <FileText size={14} className="mr-1" />
              {stmtDownloading ? 'Building...' : 'Excel'}
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={!stmtCustomerId || stmtDownloading}
              onClick={() => handleDownloadStatement('pdf')}
            >
              <FileText size={14} className="mr-1" />
              {stmtDownloading ? 'Building...' : 'PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-border/60 shadow-sm">


          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-body text-muted-foreground mb-1">Total Outstanding</p>
                <p className="text-2xl font-heading font-bold text-foreground">
                  {invoices.length}
                </p>

                <p className="text-xs text-muted-foreground mt-1">Invoices</p>
              </div>
              <div className="bg-blue-50 text-blue-600 p-2 rounded-lg">
                <FileText size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border/60 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-body text-muted-foreground mb-1">Net Outstanding</p>
                <p className="text-2xl font-heading font-bold text-foreground">
                  ₹{((invoices.reduce((sum, inv) => {
                    if (inv.short_payment_balance > 0) {
                      return sum + inv.short_payment_balance;
                    }
                    const remaining = inv.amount - (inv.cash_discount_amount || 0) - (inv.paid_amount || 0);
                    return sum + Math.max(0, remaining);
                  }, 0) + credits.reduce((sum, cr) => sum + (cr.amount || 0), 0) + debits.reduce((sum, db) => sum + (db.amount || 0), 0)) / 100000).toFixed(2)}L
                </p>
              </div>
              <div className="bg-amber-50 text-amber-600 p-2 rounded-lg">
                <FileText size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border/60 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-body text-muted-foreground mb-1">Customers 120+ Days</p>
                <p className="text-3xl font-heading font-bold text-red-600">
                  {customers120Plus.length}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Need Immediate Attention</p>
              </div>
              <div className="bg-red-50 text-red-600 p-3 rounded-lg">
                <Users size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs */}
      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="invoices">
            Outstanding ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="paid">
            Paid/Partial ({paidInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="customers">
            120+ Days ({customers120Plus.length})
          </TabsTrigger>
          <TabsTrigger value="credits">
            Credits ({credits.length})
          </TabsTrigger>
          <TabsTrigger value="debits">
            Debits ({debits.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Invoice List */}
        <TabsContent value="invoices">
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <CardTitle className="text-2xl font-heading font-semibold">
                    Outstanding Invoices ({sortedInvoices.length})
                  </CardTitle>

                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                      placeholder="Search invoice/customer/code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Filters Row */}
                <div className="flex flex-wrap gap-3 items-center">
                  {/* Region Filter */}
                  <div className="w-full sm:w-44">
                    <Select value={regionFilter} onValueChange={setRegionFilter}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Filter by region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All regions</SelectItem>
                        {regions.map((region) => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Customer Code Filter */}
                  <div className="w-full sm:w-44">
                    <Select value={customerCodeFilter} onValueChange={setCustomerCodeFilter}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Filter by code" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All codes</SelectItem>
                        {customerCodes.map((code) => (
                          <SelectItem key={code} value={code}>{code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Customer Filter */}
                  <div className="w-full sm:w-56">
                    <Select value={customerFilter} onValueChange={setCustomerFilter}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Filter by customer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All customers</SelectItem>
                        {(() => {
                          const allC = [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                          const groups = [
                            { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                            { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                            { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                            { label: 'S — Z', test: n => n >= 'S' },
                          ];
                          return groups.map(g => {
                            const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                            if (!items.length) return null;
                            return (
                              <SelectGroup key={g.label}>
                                <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                                {items.map(c => (
                                  <SelectItem key={c.customer_id} value={c.customer_id}>
                                    {(c.name || 'Unknown')}{c.customer_code ? ` (${c.customer_code})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            );
                          });
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort Dropdown */}
                  <div className="w-full sm:w-48">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date_asc">Date (Oldest First)</SelectItem>
                        <SelectItem value="date_desc">Date (Newest First)</SelectItem>
                        <SelectItem value="amount_high">Amount (High to Low)</SelectItem>
                        <SelectItem value="amount_low">Amount (Low to High)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Clear Filters Button */}
                  {(regionFilter !== 'all' || customerCodeFilter !== 'all' || customerFilter !== 'all' || sortBy !== 'date_asc') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRegionFilter('all');
                        setCustomerCodeFilter('all');
                        setCustomerFilter('all');
                        setSortBy('date_asc');
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Invoice Date</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Invoice Number</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Customer</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Code</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Region</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Amount</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Days</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Remaining</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Interest</th>
                      <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Status</th>
                      {canRecordPayment && (
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedInvoices.map((invoice, idx) => {
                      const daysFromInvoice = getDaysFromInvoice(invoice.invoice_date);
                      return (
                        <tr key={idx} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                          <td className="p-3 font-body text-foreground text-sm">
                            {new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
                          </td>
                          <td className="p-3 font-body text-foreground font-medium text-sm">
                            {invoice.invoice_number}
                            <EntryTypeBadge
                              type={invoice.transaction_type !== 'invoice' ? invoice.transaction_type : null}
                              grLinked={invoice.gr_linked}
                            />
                          </td>
                          <td className="p-3 font-body text-foreground text-sm">
                            {invoice.customer_name || 'Unknown'}
                          </td>
                          <td className="p-3 font-body text-muted-foreground text-sm">
                            {invoice.customer_code || '-'}
                          </td>
                          <td className="p-3 font-body text-muted-foreground text-sm">
                            {invoice.region || '-'}
                          </td>
                          <td className="p-3 font-body text-foreground font-semibold text-sm">
                            {invoice.short_payment_balance > 0 ? (
                              <span className="text-purple-700">
                                ₹{invoice.short_payment_balance.toLocaleString()}
                                <span className="text-[10px] ml-1 text-purple-500">(bal)</span>
                              </span>
                            ) : (
                              `₹${invoice.amount.toLocaleString()}`
                            )}
                          </td>
                          <td className="p-3">
                            <Badge className={getStatusColor(daysFromInvoice)}>
                              {daysFromInvoice} days
                            </Badge>
                          </td>
                          <td className="p-3 font-body text-foreground font-semibold">
                            {(() => {
                              // For short payment invoices, show the short_payment_balance
                              // Check both payment_status AND the actual short_payment_balance field
                              if (invoice.short_payment_balance > 0) {
                                return (
                                  <span className="text-purple-700">
                                    ₹{invoice.short_payment_balance.toLocaleString()}
                                    <span className="text-[10px] ml-1 text-purple-500">(short)</span>
                                  </span>
                                );
                              }
                              // Calculate remaining balance
                              const remaining = invoice.amount - (invoice.cash_discount_amount || 0) - (invoice.paid_amount || 0);
                              // Apply ₹10 rounding tolerance
                              return `₹${Math.max(remaining <= 10 ? 0 : remaining, 0).toLocaleString()}`;
                            })()}
                          </td>
                          <td className="p-3 font-body text-amber-600 font-semibold">
                            ₹{(invoice.interest_amount || 0).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <Badge className={
                              invoice.payment_status === 'overdue' ? 'bg-red-100 text-red-800' : 
                              (invoice.payment_status === 'paid_with_short' || invoice.short_payment_balance > 0) ? 'bg-purple-100 text-purple-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {(invoice.payment_status === 'paid_with_short' || invoice.short_payment_balance > 0) ? 'Short Bal' : invoice.payment_status}
                            </Badge>
                          </td>
                          {canRecordPayment && (
                            <td className="p-3">
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openPaymentDialog(invoice)}
                                  className="text-xs bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                                >
                                  💰 Pay
                                </Button>
                                {canEdit && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditDialog(invoice)}
                                    className="text-xs p-1 h-7 w-7"
                                  >
                                    <Edit2 size={14} />
                                  </Button>
                                )}
                                {canDelete && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openDeleteDialog(invoice)}
                                    className="text-xs p-1 h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Paid Invoices */}
        <TabsContent value="paid">
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <CardTitle className="text-2xl font-heading font-semibold">
                      Paid & Partial Payments ({sortedPaidInvoices.length})
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      View payment history, partial payments, and remaining balances
                    </p>
                  </div>

                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                      placeholder="Search invoice/customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="paid-search-input"
                    />
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3">
                  <div className="w-full sm:w-48">
                    <Select value={customerFilter} onValueChange={setCustomerFilter}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Filter by customer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All customers</SelectItem>
                        {(() => {
                          const allC = [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                          const groups = [
                            { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                            { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                            { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                            { label: 'S — Z', test: n => n >= 'S' },
                          ];
                          return groups.map(g => {
                            const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                            if (!items.length) return null;
                            return (
                              <SelectGroup key={g.label}>
                                <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                                {items.map(c => (
                                  <SelectItem key={c.customer_id} value={c.customer_id}>
                                    {(c.name || 'Unknown')}{c.customer_code ? ` (${c.customer_code})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            );
                          });
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-full sm:w-40">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date_desc">Newest first</SelectItem>
                        <SelectItem value="date_asc">Oldest first</SelectItem>
                        <SelectItem value="amount_high">Amount: High to Low</SelectItem>
                        <SelectItem value="amount_low">Amount: Low to High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {sortedPaidInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No paid invoices found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedPaidInvoices.map((invoice) => {
                    const totalPaid = invoice.payments?.reduce((sum, p) => sum + (p.payment_amount || 0), 0) || invoice.paid_amount || 0;
                    const cdAmount = invoice.cash_discount_amount || 0;
                    // Balance due should factor in CD: amount - paid - cd
                    const rawBalanceDue = (invoice.amount || 0) - totalPaid - cdAmount;
                    // Apply ₹10 rounding tolerance - if balance is less than ₹10, treat as fully paid
                    const ROUNDING_TOLERANCE = 10;
                    const balanceDue = rawBalanceDue <= ROUNDING_TOLERANCE ? 0 : rawBalanceDue;
                    const isPartiallyPaid = balanceDue > 0.01 && totalPaid > 0;
                    const isFullyPaid = balanceDue <= 0.01;
                    
                    return (
                      <div 
                        key={invoice.transaction_id} 
                        className={`border rounded-lg p-4 ${isPartiallyPaid ? 'border-amber-300 bg-amber-50/50' : 'border-green-200 bg-green-50/30'}`}
                      >
                        {/* Header Row */}
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-sm">{invoice.invoice_number}</span>
                              {isFullyPaid ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Fully Paid</Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-800 text-xs">Partial Payment</Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium mt-1">{invoice.customer_name || 'Unknown'}</p>
                            {invoice.customer_code && (
                              <p className="text-xs text-muted-foreground">{invoice.customer_code}</p>
                            )}
                          </div>
                          
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Invoice Date</p>
                            <p className="font-medium text-sm">
                              {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-IN') : '-'}
                            </p>
                          </div>
                          
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => openEditDialog(invoice)}
                              data-testid={`edit-paid-${invoice.transaction_id}`}
                            >
                              <Edit2 size={14} />
                            </Button>
                            {(user?.role === 'admin' || user?.role === 'accounts') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                onClick={() => openDeleteDialog(invoice)}
                                data-testid={`delete-paid-${invoice.transaction_id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Amount Summary */}
                        <div className="grid grid-cols-3 gap-4 mb-3 p-2 bg-white rounded border">
                          <div>
                            <p className="text-xs text-muted-foreground">Invoice Amount</p>
                            <p className="font-bold">₹{invoice.amount?.toLocaleString('en-IN') || '0'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Total Paid</p>
                            <p className="font-bold text-green-600">₹{totalPaid.toLocaleString('en-IN')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Balance Due</p>
                            <p className={`font-bold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              ₹{balanceDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                        
                        {/* Payment Details */}
                        {invoice.payments && invoice.payments.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Payment History ({invoice.payments.length})</p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {invoice.payments.map((payment, idx) => (
                                <div key={idx} className="flex justify-between items-center text-xs bg-white p-2 rounded border">
                                  <div className="flex items-center gap-3">
                                    <span className="font-medium text-green-700">₹{payment.payment_amount?.toLocaleString('en-IN')}</span>
                                    <span className="text-muted-foreground">
                                      {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN') : '-'}
                                    </span>
                                    <Badge variant="outline" className="text-xs py-0">
                                      {payment.payment_mode?.toUpperCase() || 'N/A'}
                                    </Badge>
                                  </div>
                                  <div className="text-right text-muted-foreground">
                                    {payment.payment_reference && (
                                      <span className="font-mono text-xs">{payment.payment_reference}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* CD Info if applicable */}
                        {invoice.cd_amount && invoice.cd_amount > 0 && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                            <span className="font-medium text-blue-800">CD Applied: </span>
                            <span className="text-blue-600">₹{invoice.cd_amount.toLocaleString('en-IN')} ({invoice.cd_policy || 'CD'})</span>
                          </div>
                        )}
                        
                        {/* Partial Payment Warning */}
                        {isPartiallyPaid && (
                          <div className="mt-2 p-2 bg-amber-100 rounded text-xs text-amber-800 flex items-center justify-between">
                            <span>⚠️ Balance of ₹{balanceDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })} is still outstanding</span>
                            {canRecordPayment && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-6 text-xs"
                                onClick={() => openPaymentDialog(invoice)}
                              >
                                Record Payment
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Customers 120+ Days */}
        <TabsContent value="customers">
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-heading font-semibold">
                Customers with 120+ Day Old Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customers120Plus.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No customers with 120+ day old invoices</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {customers120Plus.map((customer, idx) => (
                    <Card key={idx} className="border-red-200 shadow-sm">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-heading font-semibold text-foreground">
                              {customer.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">{customer.region}</p>
                            {customer.contact_email && (
                              <p className="text-sm text-muted-foreground mt-1">
                                📧 {customer.contact_email}
                              </p>
                            )}
                            {customer.contact_phone && (
                              <p className="text-sm text-muted-foreground">
                                📞 {customer.contact_phone}
                              </p>
                            )}
                          </div>
                          <Badge className="bg-red-100 text-red-800 text-sm">
                            Critical - {customer.oldest_invoice_days} days old
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-3 bg-red-50 rounded-lg">
                            <p className="text-xs text-red-700 mb-1">Invoices 120+ Days</p>
                            <p className="text-2xl font-heading font-bold text-red-800">
                              {customer.invoices_120_plus}
                            </p>
                          </div>
                          <div className="p-3 bg-amber-50 rounded-lg">
                            <p className="text-xs text-amber-700 mb-1">Amount Outstanding</p>
                            <p className="text-2xl font-heading font-bold text-amber-800">
                              ₹{(customer.total_amount_120_plus / 100000).toFixed(2)}L
                            </p>
                          </div>
                          <div className="p-3 bg-orange-50 rounded-lg">
                            <p className="text-xs text-orange-700 mb-1">Oldest Invoice</p>
                            <p className="text-lg font-heading font-bold text-orange-800">
                              {new Date(customer.oldest_invoice_date).toLocaleDateString('en-IN')}
                            </p>
                            <p className="text-xs text-orange-600 mt-1">
                              {customer.oldest_invoice_days} days ago
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Credits */}
        <TabsContent value="credits">
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <CardTitle className="text-2xl font-heading font-semibold">
                  Credits (Negative Balances)
                </CardTitle>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  <div className="w-full sm:w-72">
                    <Select value={customerFilter} onValueChange={setCustomerFilter}>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Filter by customer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All customers</SelectItem>
                        {(() => {
                          const allC = [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                          const groups = [
                            { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                            { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                            { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                            { label: 'S — Z', test: n => n >= 'S' },
                          ];
                          return groups.map(g => {
                            const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                            if (!items.length) return null;
                            return (
                              <SelectGroup key={g.label}>
                                <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                                {items.map(c => (
                                  <SelectItem key={c.customer_id} value={c.customer_id}>
                                    {(c.name || 'Unknown')}{c.customer_code ? ` (${c.customer_code})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            );
                          });
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                      placeholder="Search credit/customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {filteredCredits.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No credit notes found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Type</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Apply FIFO</th>

                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Reference</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Customer</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Reason</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Credit Amount</th>
                        {canEdit && <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCredits.map((item, idx) => (
                        <tr key={idx} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                          <td className="p-3">
                            {canManageCredits ? (
                              <Select
                                value={item.credit_note_type || 'credit'}
                                onValueChange={(value) => {
                                  requestConfirm({
                                    title: 'Confirm change: Credit Type',
                                    description: `Set type to "${value === 'payment' ? 'Payment (unapplied)' : value === 'gr' ? 'GR Credit Note' : 'Credit'}"?`,
                                    onConfirm: async () => {
                                      try {
                                        await axios.put(
                                          `${API}/sales/${item.transaction_id}/credit-type`,
                                          null,
                                          { params: { credit_note_type: value }, withCredentials: true }
                                        );
                                        setCredits(prev => prev.map(c => c.transaction_id === item.transaction_id ? { ...c, credit_note_type: value, credit_note_reason: value === 'payment' ? null : c.credit_note_reason } : c));
                                        toast.success('Credit type saved');
                                      } catch (error) {
                                        console.error('Failed to update credit type:', error);
                                        toast.error(errorText(error) || 'Failed to save credit type');
                                      }
                                    }
                                  });
                                }}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="credit">Credit</SelectItem>
                                  <SelectItem value="payment">Payment (unapplied)</SelectItem>
                                  <SelectItem value="gr">GR Credit Note</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-sm text-muted-foreground">{item.credit_note_type || '-'}</span>
                            )}
                          </td>
                          <td className="p-3">
                            {canManageCredits ? (
                              item.credit_applied ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">
                                    Applied (₹{(item.credit_applied_amount || 0).toLocaleString()})
                                  </span>
                                  {item.credit_apply_type === 'payment' ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive"
                                      onClick={() => confirmUndoApplyFifo(item.transaction_id)}
                                    >
                                      Undo
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">(Invoice adjustment)</span>
                                  )}
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-full"
                                  onClick={() => confirmApplyFifo(item.transaction_id)}
                                >
                                  Apply FIFO
                                </Button>
                              )
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 font-body text-foreground">
                            {item.invoice_date ? new Date(item.invoice_date).toLocaleDateString('en-IN') : '-'}
                          </td>
                          <td className="p-3 font-body text-foreground font-medium">
                            {item.invoice_number}
                            <EntryTypeBadge type={item.transaction_type} />
                          </td>
                          <td className="p-3 font-body text-foreground">
                            {item.customer_name || 'Unknown'}
                          </td>
                          <td className="p-3">
                            {canManageCredits ? (
                              item.credit_note_type === 'payment' ? (
                                <span className="text-sm text-muted-foreground">—</span>
                              ) : (
                                <Select
                                  value={item.credit_note_reason || ''}
                                  onValueChange={(value) => {
                                    if (value === '__clear__') {
                                      requestConfirm({
                                        title: 'Confirm Clear Credit Reason',
                                        description: 'Clear the credit reason for this item?',
                                        onConfirm: () => clearCreditReason(item.transaction_id)
                                      });
                                    } else {
                                      requestConfirm({
                                        title: 'Confirm change: Credit Reason',
                                        description: `Set reason to "${value}"?`,
                                        onConfirm: () => updateCreditReason(item.transaction_id, value)
                                      });
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Select reason" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__clear__">Clear</SelectItem>
                                    {creditReasonOptions.map((opt) => (
                                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                {item.credit_note_reason || '-'}
                              </span>
                            )}
                          </td>
                          <td className="p-3 font-body text-emerald-700 font-semibold">
                            ₹{Math.abs(item.amount).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditDialog(item)}
                                  className="text-xs p-1 h-7 w-7"
                                >
                                  <Edit2 size={14} />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openDeleteDialog(item)}
                                  className="text-xs p-1 h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debits Tab */}
        <TabsContent value="debits">
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <CardTitle className="text-2xl font-heading font-semibold">
                  Debit Balances (Additional Amounts Owed)
                </CardTitle>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  <div className="w-full sm:w-72">
                    <Select value={customerFilter} onValueChange={setCustomerFilter}>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Filter by customer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All customers</SelectItem>
                        {(() => {
                          const allC = [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                          const groups = [
                            { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                            { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                            { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                            { label: 'S — Z', test: n => n >= 'S' },
                          ];
                          return groups.map(g => {
                            const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                            if (!items.length) return null;
                            return (
                              <SelectGroup key={g.label}>
                                <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                                {items.map(c => (
                                  <SelectItem key={c.customer_id} value={c.customer_id}>
                                    {(c.name || 'Unknown')}{c.customer_code ? ` (${c.customer_code})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            );
                          });
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                      placeholder="Search debit/customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {debits.filter(item => {
                const matchesSearch = searchTerm === '' ||
                  item.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  customerMap[item.customer_id]?.name?.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesCustomer = customerFilter === 'all' || item.customer_id === customerFilter;
                return matchesSearch && matchesCustomer;
              }).length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No debit balances found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Reference</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Customer</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Description</th>
                        <th className="text-right p-3 font-body text-sm font-medium text-muted-foreground">Amount</th>
                        <th className="text-right p-3 font-body text-sm font-medium text-muted-foreground">Remaining</th>
                        <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debits
                        .filter(item => {
                          const matchesSearch = searchTerm === '' ||
                            item.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            customerMap[item.customer_id]?.name?.toLowerCase().includes(searchTerm.toLowerCase());
                          const matchesCustomer = customerFilter === 'all' || item.customer_id === customerFilter;
                          return matchesSearch && matchesCustomer;
                        })
                        .map((item, idx) => {
                          const remaining = item.debit_remaining !== undefined ? item.debit_remaining : Math.abs(item.amount || 0);
                          return (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                              <td className="p-3 text-sm">
                                {item.invoice_date ? new Date(item.invoice_date).toLocaleDateString('en-IN') : '-'}
                              </td>
                              <td className="p-3 text-sm font-medium">
                                {item.invoice_number || 'DN-' + item.transaction_id?.slice(-6)}
                              </td>
                              <td className="p-3 text-sm">
                                {customerMap[item.customer_id]?.name || 'Unknown'}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">
                                {item.remarks || item.transaction_type || 'Debit balance'}
                              </td>
                              <td className="p-3 text-sm text-right font-medium text-amber-700">
                                ₹{Math.abs(item.amount || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="p-3 text-sm text-right font-bold text-amber-800">
                                ₹{remaining.toLocaleString('en-IN')}
                              </td>
                              <td className="p-3">
                                {item.debit_settled ? (
                                  <Badge className="bg-green-100 text-green-800">Settled</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-800">Outstanding</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Global confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmOpen(false)}>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const fn = confirmActionRef.current;
                setConfirmOpen(false);
                if (fn) fn();
              }}
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingTransaction?.is_credit_note ? 'Credit Note' : 'Invoice'}</DialogTitle>
            <DialogDescription>
              Update invoice details, dates, status and payment information
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Invoice Number</Label>
                <Input
                  value={editForm.invoice_number || ''}
                  onChange={(e) => setEditForm({...editForm, invoice_number: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm">Amount (₹)</Label>
                <Input
                  type="number"
                  value={editForm.amount || ''}
                  onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            
            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Invoice Date</Label>
                <Input
                  type="date"
                  value={editForm.invoice_date || ''}
                  onChange={(e) => setEditForm({...editForm, invoice_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm">Due Date</Label>
                <Input
                  type="date"
                  value={editForm.due_date || ''}
                  onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            
            {/* Payment Status */}
            <div>
              <Label className="text-sm">Payment Status</Label>
              <Select
                value={editForm.payment_status || 'pending'}
                onValueChange={(value) => setEditForm({...editForm, payment_status: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Payment Details Section */}
            {editingTransaction?.payments && editingTransaction.payments.length > 0 && (
              <div className="border rounded-lg p-3 bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Payment History</Label>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setEditForm({...editForm, clearPayments: true, payment_status: 'pending'})}
                    className="h-7 text-xs"
                  >
                    Clear All Payments
                  </Button>
                </div>
                {editForm.clearPayments ? (
                  <p className="text-sm text-amber-600">Payments will be cleared on save. Status set to Pending.</p>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {editingTransaction.payments.map((payment, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm bg-background p-2 rounded">
                        <span>₹{payment.payment_amount?.toLocaleString('en-IN')}</span>
                        <span className="text-muted-foreground">
                          {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN') : '-'}
                        </span>
                        <span className="text-muted-foreground text-xs">{payment.payment_mode || '-'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label className="text-sm">Remarks</Label>
              <Input
                value={editForm.remarks || ''}
                onChange={(e) => setEditForm({...editForm, remarks: e.target.value})}
                className="mt-1"
                placeholder="Optional notes"
              />
            </div>
            
            {editingTransaction?.is_credit_note && (
              <>
                <div>
                  <Label className="text-sm">Credit Type</Label>
                  <Select
                    value={editForm.credit_note_type || 'credit'}
                    onValueChange={(value) => setEditForm({...editForm, credit_note_type: value})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Credit</SelectItem>
                      <SelectItem value="payment">Payment (unapplied)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm">Reason</Label>
                  <Select
                    value={editForm.credit_note_reason || ''}
                    onValueChange={(value) => setEditForm({...editForm, credit_note_reason: value})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {creditReasonOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateTransaction}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Transaction Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deletingTransaction?.is_credit_note ? 'credit note' : 'invoice'}?
              <br /><br />
              <strong>Invoice:</strong> {deletingTransaction?.invoice_number}<br />
              <strong>Amount:</strong> ₹{deletingTransaction?.amount?.toLocaleString()}
              <br /><br />
              <span className="text-red-600">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTransaction} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      
      {/* Payment Recording Dialog */}

      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-heading font-semibold">💰 Record Payment</DialogTitle>
            <DialogDescription>
              Enter payment details to mark invoice as paid
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2 min-h-0">
          {selectedInvoice && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-700 font-semibold text-sm">Customer: {selectedInvoice.customer_name || 'Unknown'}</span>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={multiInvoiceMode}
                    onChange={(e) => setMultiInvoiceMode(e.target.checked)}
                    className="rounded"
                  />
                  <span className="font-medium text-blue-800">Pay Multiple Invoices</span>
                </label>
              </div>
              
              {!multiInvoiceMode && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-blue-700 font-semibold">Invoice:</span>
                    <span className="ml-2 font-bold">{selectedInvoice.invoice_number}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-semibold">Invoice Date:</span>
                    <span className="ml-2 font-bold">
                      {selectedInvoice.invoice_date ? new Date(selectedInvoice.invoice_date).toLocaleDateString('en-IN') : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-semibold">Invoice Amount:</span>
                    <span className="ml-2 font-bold">₹{selectedInvoice.amount?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-semibold">Before GST (5%):</span>
                    <span className="ml-2 font-medium text-green-700">₹{(selectedInvoice.amount / 1.05).toFixed(0).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Debit Balances Section - Show in SINGLE invoice mode using dedicated API data */}
          {!multiInvoiceMode && selectedInvoice && availableDebits.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-amber-800 font-semibold text-sm">
                  Debit Balances (Additional amounts owed)
                </span>
                <span className="text-xs text-amber-600">
                  {selectedDebits.length} of {availableDebits.length} selected
                </span>
              </div>
              <p className="text-xs text-amber-700 mb-2">
                Select debit balances to settle with this payment (optional)
              </p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {availableDebits.map(debit => {
                  const remaining = debit.available_amount || debit.total_amount || 0;
                  const isSelected = selectedDebits.some(d => d.transaction_id === debit.transaction_id);
                  return (
                    <label 
                      key={debit.transaction_id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        isSelected ? 'bg-amber-100 border border-amber-300' : 'bg-white border border-amber-100 hover:bg-amber-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDebits(prev => [...prev, {
                                transaction_id: debit.transaction_id,
                                invoice_number: debit.invoice_number,
                                amount: remaining
                              }]);
                            } else {
                              setSelectedDebits(prev => prev.filter(d => d.transaction_id !== debit.transaction_id));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">{debit.invoice_number || 'DN-' + debit.transaction_id.slice(-6)}</span>
                      </div>
                      <span className="text-sm font-bold text-amber-800">₹{remaining.toLocaleString('en-IN')}</span>
                    </label>
                  );
                })}
              </div>
              {selectedDebits.length > 0 && (
                <div className="mt-2 pt-2 border-t border-amber-200 flex justify-between text-sm">
                  <span className="text-amber-700">Total Debit Selected:</span>
                  <span className="font-bold text-amber-800">
                    ₹{selectedDebits.reduce((sum, d) => sum + d.amount, 0).toLocaleString('en-IN')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Credit Balances Section - Show in SINGLE invoice mode using dedicated API data */}
          {!multiInvoiceMode && selectedInvoice && availableCredits.length > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-800 font-semibold text-sm">
                  Credit Balances (Available to adjust)
                </span>
                <span className="text-xs text-green-600">
                  {selectedCredits.length} of {availableCredits.length} selected
                </span>
              </div>
              <p className="text-xs text-green-700 mb-2">
                Select credit balances to apply against invoices (reduces amount to pay)
              </p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {availableCredits.map(credit => {
                  const available = credit.available_amount || credit.total_amount || 0;
                  const isSelected = selectedCredits.some(c => c.transaction_id === credit.transaction_id);
                  return (
                    <label 
                      key={credit.transaction_id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        isSelected ? 'bg-green-100 border border-green-300' : 'bg-white border border-green-100 hover:bg-green-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCredits(prev => [...prev, {
                                transaction_id: credit.transaction_id,
                                invoice_number: credit.reference || credit.transaction_id,
                                amount: available
                              }]);
                            } else {
                              setSelectedCredits(prev => prev.filter(c => c.transaction_id !== credit.transaction_id));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">{credit.reference || 'CN-' + credit.transaction_id.slice(-6)}</span>
                        {credit.reason && <span className="text-xs text-green-600">({credit.reason})</span>}
                      </div>
                      <span className="text-sm font-bold text-green-800">₹{available.toLocaleString('en-IN')}</span>
                    </label>
                  );
                })}
              </div>
              {selectedCredits.length > 0 && (
                <div className="mt-2 pt-2 border-t border-green-200 flex justify-between text-sm">
                  <span className="text-green-700">Total Credit to Apply:</span>
                  <span className="font-bold text-green-800">
                    -₹{selectedCredits.reduce((sum, c) => sum + c.amount, 0).toLocaleString('en-IN')}
                  </span>
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Payments</Label>
                {!multiInvoiceMode && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                    onClick={() => setPaymentForm(prev => ({
                      ...prev,
                      payments: [...prev.payments, {
                        payment_amount: '',
                        payment_date: new Date().toISOString().split('T')[0],
                        payment_mode: 'neft',
                        payment_reference: '',
                        remarks: ''
                      }]
                    }))}
                  >
                    + Add
                  </Button>
                )}
              </div>

              {paymentForm.payments.map((p, idx) => (
                <div key={idx} className="p-4 border rounded-lg space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Payment Amount *</Label>
                      <Input
                        type="number"
                        value={p.payment_amount}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_amount: v };
                            return { ...prev, payments };
                          });
                        }}
                        className="mt-1 h-10"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Payment Date *</Label>
                      <Input
                        type="date"
                        value={p.payment_date}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_date: v };
                            return { ...prev, payments };
                          });
                        }}
                        className="mt-1 h-10"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Payment Mode *</Label>
                      <Select
                        value={p.payment_mode}
                        onValueChange={(value) => {
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_mode: value };
                            return { ...prev, payments };
                          });
                        }}
                      >
                        <SelectTrigger className="mt-1 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="neft">NEFT</SelectItem>
                          <SelectItem value="rtgs">RTGS</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="imps">IMPS</SelectItem>
                          <SelectItem value="dd">Demand Draft</SelectItem>
                          <SelectItem value="credit_adjustment">Credit Adjustment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Credit Selection - shown when payment mode is credit_adjustment */}
                    {p.payment_mode === 'credit_adjustment' && (
                      <div className="col-span-2">
                        <Label className="text-sm">Select Credit Note to Apply</Label>
                        <Select
                          value={p.credit_id || ''}
                          onValueChange={(value) => {
                            const credit = creditsWithBalance.find(c => c.transaction_id === value);
                            setPaymentForm(prev => {
                              const payments = prev.payments.slice();
                              payments[idx] = { 
                                ...payments[idx], 
                                credit_id: value,
                                payment_reference: credit?.invoice_number || '',
                                // Auto-fill available credit amount
                                payment_amount: credit ? (credit.credit_remaining !== undefined ? credit.credit_remaining : Math.abs(credit.amount || 0)).toString() : ''
                              };
                              return { ...prev, payments };
                            });
                          }}
                        >
                          <SelectTrigger className="mt-1 h-10">
                            <SelectValue placeholder="Select credit note..." />
                          </SelectTrigger>
                          <SelectContent className="max-h-[250px]">
                            {creditsWithBalance
                              .filter(c => !selectedInvoice || c.customer_id === selectedInvoice.customer_id)
                              .map((credit) => {
                                const customer = customerMap[credit.customer_id];
                                const available = credit.credit_remaining !== undefined ? credit.credit_remaining : Math.abs(credit.amount || 0);
                                return (
                                  <SelectItem key={credit.transaction_id} value={credit.transaction_id}>
                                    {credit.invoice_number} - {customer?.name?.substring(0, 20) || 'Unknown'} - ₹{available.toLocaleString('en-IN')}
                                  </SelectItem>
                                );
                              })}
                          </SelectContent>
                        </Select>
                        {creditsWithBalance.filter(c => !selectedInvoice || c.customer_id === selectedInvoice.customer_id).length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">No credit notes available for this customer</p>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-sm">Reference Number</Label>
                      <Input
                        placeholder={p.payment_mode === 'credit_adjustment' ? 'Credit note number (auto-filled)' : 'Cheque number or transaction ID'}
                        value={p.payment_reference}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_reference: v };
                            return { ...prev, payments };
                          });
                        }}
                        className="mt-1 h-10"
                        disabled={p.payment_mode === 'credit_adjustment'}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm">Remarks</Label>
                    <Input
                      placeholder="Notes for this payment"
                      value={p.remarks}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPaymentForm(prev => {
                          const payments = prev.payments.slice();
                          payments[idx] = { ...payments[idx], remarks: v };
                          return { ...prev, payments };
                        });
                      }}
                      className="mt-1 h-10"
                    />
                  </div>

                  {paymentForm.payments.length > 1 && (
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setPaymentForm(prev => ({
                          ...prev,
                          payments: prev.payments.filter((_, i) => i !== idx)
                        }))}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Multi-Invoice Selection (when multi-invoice mode is ON) */}
            {multiInvoiceMode && customerInvoicesForPayment.length > 0 && (
              <div className="p-3 border rounded-lg bg-indigo-50 border-indigo-200 space-y-3">
                {/* CD Policy Info Box for Multi-Invoice Mode */}
                {(() => {
                  const customerId = selectedInvoice?.customer_id;
                  const customerInfo = customerMap[customerId] || {};
                  const customerRegion = customerInfo.region || '';
                  const invoiceDivision = selectedInvoice?.division || selectedDivision || '';
                  
                  // Get applicable CD rate
                  const customerRate = divisionCdRates?.customers?.[customerId];
                  const regionKey = `${invoiceDivision}|${customerRegion}`;
                  const regionRate = divisionCdRates?.regions?.[regionKey];
                  const divisionRate = divisionCdRates?.divisions?.[invoiceDivision];
                  const effectiveRate = customerRate || regionRate || divisionRate || {};
                  
                  const highRate = effectiveRate.high_rate ?? 5;
                  const highDays = effectiveRate.high_days ?? 15;
                  const lowRate = effectiveRate.low_rate ?? 3;
                  const lowDays = effectiveRate.low_days ?? 30;
                  
                  let rateSource = invoiceDivision || 'Default';
                  if (customerRate) {
                    rateSource = `${customerInfo.name || customerId} (Custom)`;
                  } else if (regionRate) {
                    rateSource = `${invoiceDivision} + ${customerRegion}`;
                  }
                  
                  // Warning if no region and no custom rate
                  if (!customerRate && !customerRegion) {
                    return (
                      <div className="p-2 bg-amber-100 border border-amber-300 rounded text-xs">
                        <span className="font-semibold text-amber-800">⚠️ Region Not Set:</span>
                        <span className="text-amber-700 ml-1">
                          Update customer region in Customers page for accurate CD rates.
                        </span>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="p-2 bg-green-100 border border-green-300 rounded text-xs">
                      <div className="flex flex-wrap justify-between items-center gap-2">
                        <span className="font-semibold text-green-800">
                          CD Policy ({rateSource}):
                        </span>
                        <span className="text-green-700">
                          <strong>{highRate}%</strong> within {highDays} days | 
                          <strong> {lowRate}%</strong> within {lowDays} days
                        </span>
                      </div>
                    </div>
                  );
                })()}
                
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm text-indigo-900">Select Invoices to Pay</div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={selectAllInvoicesForPayment}
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={autoAllocatePaymentFIFO}
                    >
                      Auto-FIFO
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7"
                      onClick={clearAllInvoicesForPayment}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                
                <div className="max-h-80 overflow-y-auto border rounded bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-indigo-100 sticky top-0 z-10">
                      <tr>
                        <th className="p-2 text-left w-8">✓</th>
                        <th className="p-2 text-left">Invoice</th>
                        <th className="p-2 text-right">Inv Value</th>
                        <th className="p-2 text-right">GST</th>
                        <th className="p-2 text-center">CD Policy</th>
                        <th className="p-2 text-right">CD Amt</th>
                        <th className="p-2 text-right font-semibold text-indigo-700">Net Payable</th>
                        <th className="p-2 text-right font-semibold text-green-700">Allocate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerInvoicesForPayment.map((inv) => {
                        // Get CD rates for this invoice
                        const custRate = divisionCdRates?.customers?.[selectedInvoice?.customer_id];
                        const custRegion = customerMap[selectedInvoice?.customer_id]?.region || '';
                        const regionKey = `${inv.division}|${custRegion}`;
                        const regionRate = divisionCdRates?.regions?.[regionKey];
                        const divRate = divisionCdRates?.divisions?.[inv.division];
                        const effectiveRate = custRate || regionRate || divRate || {};
                        const highRate = effectiveRate.high_rate ?? 5;
                        const lowRate = effectiveRate.low_rate ?? 3;
                        const highDays = effectiveRate.high_days ?? 15;
                        const lowDays = effectiveRate.low_days ?? 30;
                        
                        // Calculate days from invoice
                        const invDate = new Date(inv.invoice_date);
                        const payDate = new Date(paymentForm.payments[0]?.payment_date || new Date());
                        const daysDiff = Math.floor((payDate - invDate) / (1000 * 60 * 60 * 24));
                        const eligibleHigh = daysDiff <= highDays;
                        const eligibleLow = daysDiff <= lowDays;
                        const beyondCdPeriod = daysDiff > lowDays;
                        
                        // Calculate CD due dates
                        const cdHighDueDate = new Date(invDate.getTime() + highDays * 24 * 60 * 60 * 1000);
                        const cdLowDueDate = new Date(invDate.getTime() + lowDays * 24 * 60 * 60 * 1000);
                        
                        // Check if partially paid without CD (can apply CD later)
                        const isPartiallyPaid = (inv.paid_amount || 0) > 0;
                        const hasCdApplied = (inv.cash_discount_amount || 0) > 0;
                        const canApplyCdLater = isPartiallyPaid && !hasCdApplied;
                        
                        // Check if this is a short payment scenario
                        const taxableAmt = inv.amount / 1.05;
                        const gstAmount = inv.amount - taxableAmt;
                        const selectedCdRate = (inv.cd_policy?.includes('high') ? highRate : (inv.cd_policy?.includes('low') ? lowRate : 0)) / 100;
                        const cdAmount = Math.round(taxableAmt * selectedCdRate);
                        const netAfterCd = inv.remaining - cdAmount;
                        const isShortPayment = inv.cd_policy && inv.cd_policy !== 'no_cd' && (inv.allocated || 0) < (netAfterCd - 0.01);
                        
                        return (
                          <tr 
                            key={inv.transaction_id} 
                            className={`border-b hover:bg-indigo-50/50 ${inv.selected ? 'bg-indigo-50' : ''} ${isShortPayment && inv.selected ? 'bg-purple-50' : ''}`}
                          >
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={inv.selected}
                                onChange={() => toggleInvoiceForPayment(inv.transaction_id)}
                                className="rounded"
                              />
                            </td>
                            <td className="p-2">
                              <div className="font-medium">{inv.invoice_number}</div>
                              <div className="text-[10px] text-muted-foreground">
                                {new Date(inv.invoice_date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})} ({daysDiff}d)
                                {beyondCdPeriod && <span className="ml-1 text-amber-600" title="Past CD period - needs approval">⚠</span>}
                                {canApplyCdLater && <span className="ml-1 text-blue-600" title="Partially paid - CD can be applied with approval">●</span>}
                              </div>
                            </td>
                            <td className="p-2 text-right text-gray-700">
                              ₹{Math.round(taxableAmt).toLocaleString()}
                            </td>
                            <td className="p-2 text-right text-gray-500">
                              ₹{Math.round(gstAmount).toLocaleString()}
                            </td>
                            <td className="p-2">
                              {inv.selected ? (
                                <Select
                                  value={inv.cd_policy || 'no_cd'}
                                  onValueChange={(value) => {
                                    const newCdRate = (value.includes('high') ? highRate : (value.includes('low') ? lowRate : 0)) / 100;
                                    const newCdAmount = Math.round(taxableAmt * newCdRate);
                                    setCustomerInvoicesForPayment(prev => prev.map(i => 
                                      i.transaction_id === inv.transaction_id 
                                        ? { 
                                            ...i, 
                                            cd_policy: value, 
                                            cd_approval: value.includes('approval'),
                                            cd_amount: newCdAmount
                                          }
                                        : i
                                    ));
                                  }}
                                >
                                  <SelectTrigger className="h-7 w-24 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="no_cd">No CD</SelectItem>
                                    {eligibleHigh && (
                                      <SelectItem value="cd_high_15">{highRate}%</SelectItem>
                                    )}
                                    {eligibleLow && !eligibleHigh && (
                                      <SelectItem value="cd_low_30">{lowRate}%</SelectItem>
                                    )}
                                    {/* Management Approval options - show when past CD period OR partially paid */}
                                    {(beyondCdPeriod || canApplyCdLater || !eligibleHigh) && (
                                      <SelectItem value="cd_high_15_approval">{highRate}% (Mgmt Appr)</SelectItem>
                                    )}
                                    {(beyondCdPeriod || canApplyCdLater || !eligibleLow) && (
                                      <SelectItem value="cd_low_30_approval">{lowRate}% (Mgmt Appr)</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td className="p-2 text-right">
                              {inv.selected && inv.cd_policy && inv.cd_policy !== 'no_cd' ? (
                                <span className="text-green-600 font-medium">₹{cdAmount.toLocaleString()}</span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="p-2 text-right font-semibold text-indigo-700">
                              ₹{Math.round(netAfterCd).toLocaleString()}
                            </td>
                            <td className="p-2 text-right">
                              {inv.selected ? (
                                <Input
                                  type="number"
                                  value={inv.allocated || ''}
                                  placeholder="0"
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    const maxAllocation = Math.max(0, netAfterCd);
                                    const cappedVal = Math.min(val, maxAllocation);
                                    setCustomerInvoicesForPayment(prev => prev.map(i => 
                                      i.transaction_id === inv.transaction_id 
                                        ? { ...i, allocated: cappedVal, cd_amount: cdAmount }
                                        : i
                                    ));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`h-7 w-24 text-xs text-right ${isShortPayment ? 'border-purple-400 bg-purple-50' : ''}`}
                                />
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Allocation Summary */}
                {(() => {
                  const selectedInvs = customerInvoicesForPayment.filter(inv => inv.selected);
                  const totalAllocated = selectedInvs.reduce((sum, inv) => sum + (inv.allocated || 0), 0);
                  const totalRemaining = selectedInvs.reduce((sum, inv) => sum + (inv.remaining || 0), 0);
                  const totalPayment = paymentForm.payments.reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0);
                  
                  // Calculate total pre-GST and GST amounts for selected invoices
                  const totalPreGst = selectedInvs.reduce((sum, inv) => sum + (inv.amount / 1.05), 0);
                  const totalGst = selectedInvs.reduce((sum, inv) => sum + (inv.amount - inv.amount / 1.05), 0);
                  
                  // Calculate total CD amount for selected invoices with CD policy
                  const totalCdAmount = selectedInvs.reduce((sum, inv) => {
                    if (inv.cd_policy && inv.cd_policy !== 'no_cd') {
                      return sum + (inv.cd_amount || 0);
                    }
                    return sum;
                  }, 0);
                  
                  // Net payable = Total Outstanding - CD
                  const totalNetPayable = totalRemaining - totalCdAmount;
                  const shortBalance = totalNetPayable - totalAllocated;
                  const allocationMismatch = Math.abs(totalAllocated - totalPayment) > 10;
                  const cdInvoiceCount = selectedInvs.filter(inv => inv.cd_policy && inv.cd_policy !== 'no_cd').length;
                  
                  return (
                    <div className="mt-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {/* Left Column - Invoice Summary */}
                        <div className="space-y-2">
                          <h4 className="font-semibold text-indigo-800 border-b border-indigo-200 pb-1">Invoice Summary</h4>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Invoices Selected:</span>
                            <span className="font-medium">{selectedInvs.length}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Pre-GST:</span>
                            <span className="font-medium">₹{Math.round(totalPreGst).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total GST:</span>
                            <span className="font-medium">₹{Math.round(totalGst).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Outstanding:</span>
                            <span className="font-semibold">₹{Math.round(totalRemaining).toLocaleString()}</span>
                          </div>
                          {totalCdAmount > 0 && (
                            <div className="flex justify-between text-green-700">
                              <span>Less: CD ({cdInvoiceCount} inv):</span>
                              <span className="font-semibold">- ₹{totalCdAmount.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-1 border-t border-indigo-200">
                            <span className="font-semibold text-indigo-800">Net Payable:</span>
                            <span className="font-bold text-indigo-800">₹{Math.round(totalNetPayable).toLocaleString()}</span>
                          </div>
                        </div>
                        
                        {/* Right Column - Payment Summary */}
                        <div className="space-y-2">
                          <h4 className="font-semibold text-indigo-800 border-b border-indigo-200 pb-1">Payment Summary</h4>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Payment Amount:</span>
                            <span className="font-medium">₹{totalPayment.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Allocated:</span>
                            <span className={`font-medium ${allocationMismatch ? 'text-red-600' : 'text-green-600'}`}>
                              ₹{totalAllocated.toLocaleString()}
                            </span>
                          </div>
                          {shortBalance > 10 && (
                            <div className="flex justify-between text-purple-700 pt-1 border-t border-purple-200">
                              <span className="font-semibold">Short Balance:</span>
                              <span className="font-bold">₹{Math.round(shortBalance).toLocaleString()}</span>
                            </div>
                          )}
                          {allocationMismatch && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                              ⚠️ Total Allocated must equal Payment Amount
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Legend */}
                      <div className="mt-3 pt-2 border-t border-indigo-200 text-[10px] text-gray-500 flex gap-4">
                        <span>⚠ Past CD period (needs approval)</span>
                        <span>● Partially paid (CD with approval)</span>
                        <span className="text-purple-600">Purple = Short payment</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            
            {/* Debit & Credit Balances Section - Show AFTER invoice table in MULTI-invoice mode */}
            {/* Uses dedicated API data (not division-filtered) to ensure all customer debits/credits are shown */}
            {multiInvoiceMode && selectedInvoice && availableDebits.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-amber-800 font-semibold text-sm">
                    Debit Balances (Additional amounts owed)
                  </span>
                  <span className="text-xs text-amber-600">
                    {selectedDebits.length} of {availableDebits.length} selected
                  </span>
                </div>
                <p className="text-xs text-amber-700 mb-2">
                  Select debit balances to settle with this payment (optional)
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {availableDebits.map(debit => {
                    const remaining = debit.available_amount || debit.total_amount || 0;
                    const isSelected = selectedDebits.some(d => d.transaction_id === debit.transaction_id);
                    return (
                      <label 
                        key={debit.transaction_id}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                          isSelected ? 'bg-amber-100 border border-amber-300' : 'bg-white border border-amber-100 hover:bg-amber-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDebits(prev => [...prev, {
                                  transaction_id: debit.transaction_id,
                                  invoice_number: debit.invoice_number,
                                  amount: remaining
                                }]);
                              } else {
                                setSelectedDebits(prev => prev.filter(d => d.transaction_id !== debit.transaction_id));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm font-medium">{debit.invoice_number || 'DN-' + debit.transaction_id.slice(-6)}</span>
                        </div>
                        <span className="text-sm font-bold text-amber-800">₹{remaining.toLocaleString('en-IN')}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedDebits.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-amber-200 flex justify-between text-sm">
                    <span className="text-amber-700">Total Debit Selected:</span>
                    <span className="font-bold text-amber-800">
                      ₹{selectedDebits.reduce((sum, d) => sum + d.amount, 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>
            )}

            {multiInvoiceMode && selectedInvoice && availableCredits.length > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-800 font-semibold text-sm">
                    Credit Balances (Available to adjust)
                  </span>
                  <span className="text-xs text-green-600">
                    {selectedCredits.length} of {availableCredits.length} selected
                  </span>
                </div>
                <p className="text-xs text-green-700 mb-2">
                  Select credit balances to apply against invoices (reduces amount to pay)
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {availableCredits.map(credit => {
                    const available = credit.available_amount || credit.total_amount || 0;
                    const isSelected = selectedCredits.some(c => c.transaction_id === credit.transaction_id);
                    return (
                      <label 
                        key={credit.transaction_id}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                          isSelected ? 'bg-green-100 border border-green-300' : 'bg-white border border-green-100 hover:bg-green-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCredits(prev => [...prev, {
                                  transaction_id: credit.transaction_id,
                                  invoice_number: credit.reference || credit.transaction_id,
                                  amount: available
                                }]);
                              } else {
                                setSelectedCredits(prev => prev.filter(c => c.transaction_id !== credit.transaction_id));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm font-medium">{credit.reference || 'CN-' + credit.transaction_id.slice(-6)}</span>
                          {credit.reason && <span className="text-xs text-green-600">({credit.reason})</span>}
                        </div>
                        <span className="text-sm font-bold text-green-800">₹{available.toLocaleString('en-IN')}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedCredits.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-green-200 flex justify-between text-sm">
                    <span className="text-green-700">Total Credit to Apply:</span>
                    <span className="font-bold text-green-800">
                      -₹{selectedCredits.reduce((sum, c) => sum + c.amount, 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>
            )}

            {!multiInvoiceMode && (
              <>
              <div>
              <Label htmlFor="cd_policy" className="text-sm font-semibold">Cash Discount Policy</Label>
              
              {/* Calculate days from invoice date to payment date */}
              {(() => {
                const invoiceDate = selectedInvoice?.invoice_date ? new Date(selectedInvoice.invoice_date) : null;
                const paymentDate = paymentForm.payments[0]?.payment_date ? new Date(paymentForm.payments[0].payment_date) : null;
                const daysDiff = invoiceDate && paymentDate ? Math.floor((paymentDate - invoiceDate) / (1000 * 60 * 60 * 24)) : null;
                const invoiceAmount = selectedInvoice?.amount || 0;
                const taxableAmount = invoiceAmount / 1.05; // Before GST
                
                // Get division and customer's region from the invoice
                const invoiceDivision = selectedInvoice?.division || '';
                const customerId = selectedInvoice?.customer_id;
                const customerInfo = customerMap[customerId] || {};
                const customerRegion = customerInfo.region || '';
                const customerName = customerInfo.name || 'Unknown Customer';
                
                // Check for customer-specific rate first (highest priority)
                const customerRate = divisionCdRates?.customers?.[customerId];
                
                // If customer has custom rate, skip region check
                // If no custom rate and no region, show warning
                if (!customerRate && !customerRegion) {
                  return (
                    <div className="mt-2 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                      <p className="text-sm font-semibold text-amber-800 mb-2">⚠️ Region Not Set</p>
                      <p className="text-xs text-amber-700 mb-3">
                        Customer <strong>"{customerName}"</strong> does not have a region assigned. 
                        CD rates are determined by Division + Region. Please update the customer's region in the 
                        <strong> Customers</strong> page before applying CD.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        You can still record payment without CD, or go to Customers → Find this customer → Edit → Set Region.
                      </p>
                    </div>
                  );
                }
                
                // CD Rate lookup priority: Customer > Division+Region > Division > Default
                const regionKey = `${invoiceDivision}|${customerRegion}`;
                const regionRate = divisionCdRates?.regions?.[regionKey];
                const divisionRate = divisionCdRates?.divisions?.[invoiceDivision];
                const effectiveRate = customerRate || regionRate || divisionRate || {};
                
                const highRate = effectiveRate.high_rate ?? 5;
                const highDays = effectiveRate.high_days ?? 15;
                const lowRate = effectiveRate.low_rate ?? 3;
                const lowDays = effectiveRate.low_days ?? 30;
                
                // Determine rate source for display
                let rateSource = invoiceDivision;
                if (customerRate) {
                  rateSource = `${customerName} (Custom)`;
                } else if (regionRate) {
                  rateSource = `${invoiceDivision} + ${customerRegion}`;
                }
                
                // Check if invoice is partially paid without CD
                const isPartiallyPaid = (selectedInvoice?.paid_amount || 0) > 0;
                const hasCdAlreadyApplied = selectedInvoice?.cash_discount_amount > 0 || selectedInvoice?.cash_discount_applied;
                const canApplyCdLater = isPartiallyPaid && !hasCdAlreadyApplied;
                
                // Calculate CD due dates from invoice date
                const cdHighDate = invoiceDate ? new Date(invoiceDate.getTime() + highDays * 24 * 60 * 60 * 1000) : null;
                const cdLowDate = invoiceDate ? new Date(invoiceDate.getTime() + lowDays * 24 * 60 * 60 * 1000) : null;
                
                // Determine eligibility
                const eligibleForHighRate = daysDiff !== null && daysDiff <= highDays;
                const eligibleForLowRate = daysDiff !== null && daysDiff <= lowDays;
                const beyondAllCdPeriods = daysDiff !== null && daysDiff > lowDays;
                
                // CD amounts based on effective rate
                const cdHighAmount = Math.round(taxableAmount * (highRate / 100));
                const cdLowAmount = Math.round(taxableAmount * (lowRate / 100));
                
                // Calculate payment and check for short payment
                const paymentsEntered = paymentForm.payments.reduce((sum, p) => sum + (Number(p.payment_amount) || 0), 0);
                const alreadyPaid = selectedInvoice?.paid_amount || 0;
                const totalPaidWithThis = alreadyPaid + paymentsEntered;
                
                // Determine selected CD amount based on policy
                let selectedCdAmount = 0;
                if (paymentForm.cd_policy.includes('high') || paymentForm.cd_policy.includes('5') || paymentForm.cd_policy.includes('4')) {
                  selectedCdAmount = cdHighAmount;
                } else if (paymentForm.cd_policy.includes('low') || paymentForm.cd_policy.includes('3') || paymentForm.cd_policy.includes('2')) {
                  selectedCdAmount = cdLowAmount;
                }
                
                const expectedNet = invoiceAmount - (paymentForm.cd_policy !== 'no_cd' ? selectedCdAmount : 0);
                const isShortPayment = paymentForm.cd_policy !== 'no_cd' && totalPaidWithThis < (expectedNet - 0.01);
                
                return (
                  <div className="space-y-3 mt-2">
                    {/* CD Policy Information Box */}
                    <div className="p-3 border rounded-lg bg-slate-50">
                      <p className="text-xs font-semibold text-slate-700 mb-2">
                        CD Policy ({rateSource || 'Unknown'}): {highRate}% in {highDays} days / {lowRate}% in {lowDays} days
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className={`p-2 rounded ${eligibleForHighRate ? 'bg-green-100 border border-green-300' : 'bg-gray-100'}`}>
                          <p className="font-medium">{highRate}% CD (₹{cdHighAmount.toLocaleString('en-IN')})</p>
                          <p className="text-muted-foreground">
                            Pay by: {cdHighDate ? cdHighDate.toLocaleDateString('en-IN') : '-'}
                          </p>
                          {eligibleForHighRate && <p className="text-green-700 font-semibold mt-1">✓ Eligible</p>}
                        </div>
                        <div className={`p-2 rounded ${eligibleForLowRate && !eligibleForHighRate ? 'bg-green-100 border border-green-300' : 'bg-gray-100'}`}>
                          <p className="font-medium">{lowRate}% CD (₹{cdLowAmount.toLocaleString('en-IN')})</p>
                          <p className="text-muted-foreground">
                            Pay by: {cdLowDate ? cdLowDate.toLocaleDateString('en-IN') : '-'}
                          </p>
                          {eligibleForLowRate && !eligibleForHighRate && <p className="text-green-700 font-semibold mt-1">✓ Eligible</p>}
                        </div>
                      </div>
                      {daysDiff !== null && (
                        <p className={`text-xs mt-2 font-medium ${beyondAllCdPeriods ? 'text-red-600' : eligibleForHighRate ? 'text-green-600' : 'text-amber-600'}`}>
                          Payment is {daysDiff} days from invoice date
                          {eligibleForHighRate && ` → ${highRate}% CD applicable`}
                          {eligibleForLowRate && !eligibleForHighRate && ` → ${lowRate}% CD applicable`}
                          {beyondAllCdPeriods && ' → Beyond CD period'}
                        </p>
                      )}
                      {/* Show notice for partially paid invoices without CD */}
                      {canApplyCdLater && (
                        <p className="text-xs mt-2 text-blue-600 font-medium">
                          ℹ️ Invoice is partially paid without CD. You can still apply CD with management approval.
                        </p>
                      )}
                    </div>
                    
                    {/* CD Selection - show options including management approval for partial payments and higher CD */}
                    {loadingCdPolicies ? (
                      <p className="text-sm text-muted-foreground">Loading CD policies...</p>
                    ) : (
                      <div>
                        <Label className="text-xs">Apply CD:</Label>
                        <Select
                          value={paymentForm.cd_policy}
                          onValueChange={(value) => setPaymentForm({...paymentForm, cd_policy: value})}
                        >
                          <SelectTrigger className={`mt-1 h-9 ${
                            paymentForm.cd_policy !== 'no_cd' && (eligibleForHighRate || eligibleForLowRate) 
                              ? 'bg-green-50 border-green-300' 
                              : paymentForm.cd_policy !== 'no_cd' && (beyondAllCdPeriods || canApplyCdLater)
                                ? 'bg-amber-50 border-amber-300' 
                                : ''
                          }`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no_cd">No CD</SelectItem>
                            {/* Standard eligible options */}
                            {eligibleForHighRate && <SelectItem value="cd_high_15">{highRate}% CD (within 15 days)</SelectItem>}
                            {eligibleForLowRate && <SelectItem value="cd_low_30">{lowRate}% CD (within 30 days)</SelectItem>}
                            
                            {/* Higher CD with approval: Show high rate approval option when only eligible for low rate */}
                            {eligibleForLowRate && !eligibleForHighRate && (
                              <SelectItem value="cd_high_15_approval">{highRate}% CD (with management approval - higher rate)</SelectItem>
                            )}
                            
                            {/* Show approval options if beyond CD period OR if partially paid without CD */}
                            {(beyondAllCdPeriods || canApplyCdLater) && (
                              <>
                                <SelectItem value="cd_high_15_approval">{highRate}% CD (with management approval)</SelectItem>
                                <SelectItem value="cd_low_30_approval">{lowRate}% CD (with management approval)</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Management Approval section - show when selecting CD with approval */}
                    {(paymentForm.cd_policy.includes('_approval')) && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            id="close_cd_approval"
                            checked={paymentForm.close_cd_after_management_approval}
                            onChange={(e) => setPaymentForm({...paymentForm, close_cd_after_management_approval: e.target.checked})}
                            className="rounded border-amber-400 mt-1"
                          />
                          <label htmlFor="close_cd_approval" className="text-xs text-amber-900">
                            <span className="font-semibold">Management Approval Confirmation</span>
                            <br />
                            {eligibleForLowRate && !eligibleForHighRate && paymentForm.cd_policy.includes('high') ? (
                              <span>Eligible for {lowRate}% CD but requesting higher {highRate}% rate with management approval.</span>
                            ) : canApplyCdLater ? (
                              <span>Invoice is partially paid. Applying CD now requires management approval.</span>
                            ) : (
                              <span>Payment is {daysDiff} days from invoice. Policy allows {highRate}% in {highDays} days or {lowRate}% in {lowDays} days.</span>
                            )}
                            <br />
                            <span className="font-medium">
                              Applying: {paymentForm.cd_policy.includes('high') ? highRate : lowRate}% CD 
                              (₹{paymentForm.cd_policy.includes('high') ? cdHighAmount.toLocaleString('en-IN') : cdLowAmount.toLocaleString('en-IN')})
                              with management approval
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                    
                    {/* Short Payment Option - show when CD is applied but payment is less than expected */}
                    {isShortPayment && paymentForm.cd_policy !== 'no_cd' && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            id="allow_short_payment"
                            checked={paymentForm.allow_cd_with_short_payment}
                            onChange={(e) => setPaymentForm({...paymentForm, allow_cd_with_short_payment: e.target.checked})}
                            className="rounded border-purple-400 mt-1"
                          />
                          <label htmlFor="allow_short_payment" className="text-xs text-purple-900">
                            <span className="font-semibold">Allow CD with Short Payment</span>
                            <br />
                            <span>Payment (₹{totalPaidWithThis.toLocaleString('en-IN')}) is less than net amount (₹{Math.round(expectedNet).toLocaleString('en-IN')}) after CD.</span>
                            <br />
                            <span className="font-medium text-purple-700">
                              Balance of ₹{Math.round(expectedNet - totalPaidWithThis).toLocaleString('en-IN')} will remain unpaid 
                              (to be reconciled against GR/Defective/Incentive/Scheme via Excel).
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Live remaining preview - compact */}
            {selectedInvoice && (
              <div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
                {(() => {
                  const invoiceAmount = selectedInvoice.amount || 0;
                  const taxableAmount = invoiceAmount / 1.05;
                  const alreadyPaid = (selectedInvoice.paid_amount || 0);
                  const existingCd = (selectedInvoice.cash_discount_amount || 0);
                  
                  // Get division+region specific rates from database
                  const invDivision = selectedInvoice?.division || '';
                  const custInfo = customerMap[selectedInvoice?.customer_id] || {};
                  const custRegion = custInfo.region || '';
                  const regionKeyPreview = `${invDivision}|${custRegion}`;
                  // Check for customer-specific rate first
                  const customerRatePreview = divisionCdRates?.customers?.[selectedInvoice?.customer_id];
                  const regionRatePreview = divisionCdRates?.regions?.[regionKeyPreview];
                  const divRatePreview = divisionCdRates?.divisions?.[invDivision];
                  const effectiveRatePreview = customerRatePreview || regionRatePreview || divRatePreview || {};
                  
                  const highRatePreview = (effectiveRatePreview.high_rate ?? 5) / 100;
                  const lowRatePreview = (effectiveRatePreview.low_rate ?? 3) / 100;
                  
                  // Calculate CD amount if being applied now
                  let cdAmountToApply = 0;
                  if (paymentForm.cd_policy !== 'no_cd' && !existingCd) {
                    if (paymentForm.cd_policy.includes('high') || paymentForm.cd_policy.includes('5') || paymentForm.cd_policy.includes('4')) {
                      cdAmountToApply = Math.round(taxableAmount * highRatePreview);
                    } else if (paymentForm.cd_policy.includes('low') || paymentForm.cd_policy.includes('3') || paymentForm.cd_policy.includes('2')) {
                      cdAmountToApply = Math.round(taxableAmount * lowRatePreview);
                    }
                  }
                  
                  const totalCd = existingCd + cdAmountToApply;
                  const netInvoiceAmount = invoiceAmount - totalCd;
                  const remainingBefore = Math.max(netInvoiceAmount - alreadyPaid, 0);
                  const creditsSelectedTotal = selectedCredits.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
                  const creditsAppliedPreview = Math.min(remainingBefore, creditsSelectedTotal);
                  const paymentsEntered = paymentForm.payments.reduce((sum, p) => sum + (Number(p.payment_amount) || 0), 0);
                  const remainingAfter = Math.max(remainingBefore - creditsAppliedPreview - paymentsEntered, 0);

                  return (
                    <div className="text-xs text-blue-900 space-y-0.5">
                      <div className="flex justify-between">
                        <span>Invoice Amount</span>
                        <span className="font-semibold">₹{invoiceAmount.toLocaleString()}</span>
                      </div>
                      {totalCd > 0 && (
                        <div className="flex justify-between text-green-700">
                          <span>CD Deduction {cdAmountToApply > 0 ? '(applying now)' : '(already applied)'}</span>
                          <span>- ₹{totalCd.toLocaleString()}</span>
                        </div>
                      )}
                      {alreadyPaid > 0 && (
                        <div className="flex justify-between text-gray-600">
                          <span>Already Paid</span>
                          <span>- ₹{alreadyPaid.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Outstanding Balance</span>
                        <span className="font-semibold">₹{remainingBefore.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>This Payment (Credits + Cash)</span>
                        <span>- ₹{(creditsAppliedPreview + paymentsEntered).toLocaleString()}</span>
                      </div>
                      <div className="pt-1 border-t border-blue-200 flex justify-between">
                        <span className="font-semibold">Balance after payment</span>
                        <span className={`font-bold ${remainingAfter === 0 ? 'text-emerald-700' : 'text-amber-600'}`}>
                          ₹{remainingAfter.toLocaleString()} {remainingAfter === 0 && '✓'}
                          {remainingAfter > 0 && paymentForm.allow_cd_with_short_payment && ' (Short)'}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Adjust Credit Balances */}
            {canRecordPayment && selectedInvoice && (
              <div className="p-3 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-semibold text-sm">Adjust Credit Balances</div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => setSelectedCredits([])}
                  >
                    Clear
                  </Button>
                </div>

                {availableCredits.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No credits available.</div>
                ) : (
                  <div className="space-y-2">
                    <Select
                      value=""
                      onValueChange={(value) => {
                        const selected = availableCredits.find((c) => c.transaction_id === value);
                        if (!selected) return;
                        if (selectedCredits.some((c) => c.transaction_id === selected.transaction_id)) return;

                        setSelectedCredits((prev) => ([
                          ...prev,
                          {
                            transaction_id: selected.transaction_id,
                            label: `₹${(selected.available_amount || selected.total_amount || 0).toLocaleString()}`,
                            amount: selected.available_amount || selected.total_amount || 0
                          }
                        ]));
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select credit to apply" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCredits.map((c) => (
                          <SelectItem key={c.transaction_id} value={c.transaction_id}>
                            ₹{(c.available_amount || c.total_amount || 0).toLocaleString()}
                            {c.reason ? ` - ${c.reason}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedCredits.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {selectedCredits.map((c) => (
                          <span key={c.transaction_id} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                            {c.label}
                            <button
                              type="button"
                              className="text-green-600 hover:text-red-600"
                              onClick={() => setSelectedCredits((prev) => prev.filter((x) => x.transaction_id !== c.transaction_id))}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            
              </>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-4 border-t bg-background relative z-10">
            <Button variant="outline" onClick={() => { setPaymentDialogOpen(false); setMultiInvoiceMode(false); setIsSubmittingPayment(false); }} size="sm">
              Cancel
            </Button>
            {multiInvoiceMode ? (
              <Button 
                onClick={handleMultiInvoicePayment} 
                className="bg-indigo-600 hover:bg-indigo-700" 
                size="sm"
                disabled={isSubmittingPayment}
                type="button"
              >
                {isSubmittingPayment ? (
                  <>Processing...</>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-1" />
                    Pay Selected Invoices
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleRecordPayment} className="bg-green-600 hover:bg-green-700" size="sm"
                disabled={isSubmittingPayment} type="button">
                <CheckCircle size={16} className="mr-1" />
                {isSubmittingPayment ? 'Saving...' : 'Record Payment'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Square Off / Reconcile Dialog */}
      <Dialog open={squareOffDialogOpen} onOpenChange={(open) => {
        setSquareOffDialogOpen(open);
        if (!open) {
          setSquareOffSearch('');
          setSquareOffInvoice(null);
          setSquareOffCredit(null);
        }
      }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Square Off Invoice vs Credit</DialogTitle>
            <DialogDescription>
              Reconcile an outstanding invoice balance against a credit balance (GR/Defective/Incentive/Scheme)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search Box */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Search Customer / Invoice</Label>
              <Input
                placeholder="Type customer name or invoice number..."
                value={squareOffSearch}
                onChange={(e) => setSquareOffSearch(e.target.value)}
                className="h-10"
                data-testid="square-off-search"
              />
            </div>
            
            {/* Invoice Selection */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Select Invoice with Balance</Label>
              <Select
                value={squareOffInvoice?.transaction_id || ''}
                onValueChange={(value) => {
                  const inv = invoicesWithBalance.find(i => i.transaction_id === value);
                  setSquareOffInvoice(inv);
                  setSquareOffCredit(null); // Reset credit when invoice changes
                }}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select invoice..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {invoicesWithBalance
                    .filter(inv => {
                      if (!squareOffSearch) return true;
                      const customer = customerMap[inv.customer_id];
                      const searchLower = squareOffSearch.toLowerCase();
                      return (
                        (customer?.name || '').toLowerCase().includes(searchLower) ||
                        (inv.invoice_number || '').toLowerCase().includes(searchLower) ||
                        (inv.account_number || '').toLowerCase().includes(searchLower)
                      );
                    })
                    .map((inv) => {
                      const customer = customerMap[inv.customer_id];
                      const paid = inv.paid_amount || 0;
                      const cd = inv.cash_discount_amount || 0;
                      const shortBal = inv.short_payment_balance || 0;
                      const balance = Math.max(inv.amount - paid - cd, shortBal);
                      return (
                        <SelectItem key={inv.transaction_id} value={inv.transaction_id}>
                          {inv.invoice_number} - {customer?.name || 'Unknown'} - Balance: ₹{balance.toLocaleString('en-IN')}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              
              {squareOffInvoice && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="font-medium">Invoice:</span> {squareOffInvoice.invoice_number}</div>
                    <div><span className="font-medium">Customer:</span> {customerMap[squareOffInvoice.customer_id]?.name || 'Unknown'}</div>
                    <div><span className="font-medium">Invoice Amt:</span> ₹{(squareOffInvoice.amount || 0).toLocaleString('en-IN')}</div>
                    <div><span className="font-medium">Paid:</span> ₹{(squareOffInvoice.paid_amount || 0).toLocaleString('en-IN')}</div>
                    <div><span className="font-medium">CD Applied:</span> ₹{(squareOffInvoice.cash_discount_amount || 0).toLocaleString('en-IN')}</div>
                    <div className="text-red-600 font-semibold">
                      <span className="font-medium">Outstanding:</span> ₹{Math.max(
                        (squareOffInvoice.amount || 0) - (squareOffInvoice.paid_amount || 0) - (squareOffInvoice.cash_discount_amount || 0),
                        squareOffInvoice.short_payment_balance || 0
                      ).toLocaleString('en-IN')}
                    </div>
                  </div>
                  {squareOffInvoice.is_short_payment && (
                    <p className="mt-2 text-purple-700 font-medium">
                      ⚠️ This invoice has CD applied with short payment balance
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Credit Selection - only show if invoice is selected */}
            {squareOffInvoice && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Select Credit Balance</Label>
                {creditsForSelectedInvoice.length === 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    No credit balances available for this customer ({customerMap[squareOffInvoice.customer_id]?.name})
                  </div>
                ) : (
                  <>
                    <Select
                      value={squareOffCredit?.transaction_id || ''}
                      onValueChange={(value) => {
                        const cr = creditsForSelectedInvoice.find(c => c.transaction_id === value);
                        setSquareOffCredit(cr);
                      }}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select credit..." />
                      </SelectTrigger>
                      <SelectContent>
                        {creditsForSelectedInvoice.map((cr) => {
                          const available = cr.credit_remaining !== undefined ? cr.credit_remaining : Math.abs(cr.amount || 0);
                          return (
                            <SelectItem key={cr.transaction_id} value={cr.transaction_id}>
                              {cr.invoice_number || cr.transaction_id.slice(0,8)} - 
                              {cr.credit_note_reason || 'Unspecified'} - 
                              Available: ₹{available.toLocaleString('en-IN')}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    
                    {squareOffCredit && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="font-medium">Reference:</span> {squareOffCredit.invoice_number || squareOffCredit.transaction_id.slice(0,12)}</div>
                          <div><span className="font-medium">Reason:</span> {squareOffCredit.credit_note_reason || 'Unspecified'}</div>
                          <div><span className="font-medium">Total Credit:</span> ₹{Math.abs(squareOffCredit.amount || 0).toLocaleString('en-IN')}</div>
                          <div className="text-green-700 font-semibold">
                            <span className="font-medium">Available:</span> ₹{(
                              squareOffCredit.credit_remaining !== undefined 
                                ? squareOffCredit.credit_remaining 
                                : Math.abs(squareOffCredit.amount || 0)
                            ).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Preview of Square Off Result */}
            {squareOffInvoice && squareOffCredit && (
              <div className="p-4 bg-slate-100 border border-slate-300 rounded-lg">
                <p className="text-sm font-semibold mb-3">Square Off Preview</p>
                {(() => {
                  const invoiceOutstanding = Math.max(
                    (squareOffInvoice.amount || 0) - (squareOffInvoice.paid_amount || 0) - (squareOffInvoice.cash_discount_amount || 0),
                    squareOffInvoice.short_payment_balance || 0
                  );
                  const creditAvailable = squareOffCredit.credit_remaining !== undefined 
                    ? squareOffCredit.credit_remaining 
                    : Math.abs(squareOffCredit.amount || 0);
                  const applyAmount = Math.min(invoiceOutstanding, creditAvailable);
                  const invoiceRemaining = Math.max(invoiceOutstanding - applyAmount, 0);
                  const creditRemaining = Math.max(creditAvailable - applyAmount, 0);

                  return (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Invoice Outstanding:</span>
                        <span className="font-medium">₹{invoiceOutstanding.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Credit Available:</span>
                        <span className="font-medium">₹{creditAvailable.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between text-indigo-700 font-semibold">
                        <span>Amount to Apply:</span>
                        <span>₹{applyAmount.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Invoice Balance After:</span>
                        <span className={invoiceRemaining === 0 ? 'text-green-600 font-semibold' : 'text-amber-600'}>
                          ₹{invoiceRemaining.toLocaleString('en-IN')} {invoiceRemaining === 0 && '✓ Settled'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Credit Balance After:</span>
                        <span className={creditRemaining === 0 ? 'text-green-600 font-semibold' : 'text-amber-600'}>
                          ₹{creditRemaining.toLocaleString('en-IN')} {creditRemaining === 0 && '✓ Fully Used'}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSquareOffDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSquareOff}
              disabled={!squareOffInvoice || !squareOffCredit || squareOffLoading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {squareOffLoading ? 'Processing...' : 'Square Off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
